import { moreFromLexis as moreFromLexisWith } from '../OwnerReport/moreFromLexis'
import BUSINESS_MORE_FIELDS from './moreFromLexisFields'

const moreFromLexis = (records, root, title) => moreFromLexisWith(records, root, title, BUSINESS_MORE_FIELDS)

const list = (value) => (Array.isArray(value) ? value.filter(v => v != null) : value ? [value] : [])
const pad = (n) => String(n).padStart(2, '0')
const scalar = (value) => (typeof value === 'string' || typeof value === 'number' ? value : '')
const digits = (value) => String(value || '').replace(/\D/g, '')

const KEEP_UPPER = new Set(['LLC', 'LP', 'LLP', 'PC', 'PLLC', 'PA', 'DBA', 'II', 'III', 'IV', 'USA', 'US', 'CEO', 'CFO', 'COO', 'CTO', 'VP', 'SR', 'JR', 'NA', 'UCC', 'EIN', 'OTC', 'PIR', 'PO', 'FCC', 'DEA', 'ATF', 'IRS', 'DOT', 'FMCSA', 'SBA', 'DBT', 'MPH', 'DUI', 'DWI', 'OWI', 'CDS', 'CU', 'MD', 'RN', 'LPN', 'APRN', 'CNA', 'DDS', 'NEC', 'NE', 'NW', 'SE', 'SW', 'SFR', 'PD'])

const SMALL_WORDS = new Set(['of', 'and', 'the', 'for', 'to', 'in', 'on', 'at', 'by', 'or', 'a', 'an'])

export const titleCase = (value) => {
    if (typeof value === 'number') return String(value)
    if (!value || typeof value !== 'string') return ''
    return value.toLowerCase().replace(/[a-z0-9&'.]+/g, (word, offset, text) => {
        const bare = word.replace(/\./g, '').toUpperCase()
        if (KEEP_UPPER.has(bare)) return word.toUpperCase()
        const hyphenated = text[offset - 1] === '-' || text[offset + word.length] === '-'
        const initial = word === 'a' && !/\b(of|and|for|to|in|on|at|by|or|with)\s+$/.test(text.slice(0, offset))
        if (offset > 0 && !hyphenated && !initial && SMALL_WORDS.has(word)) return word
        return word.charAt(0).toUpperCase() + word.slice(1)
    }).replace(/\bMc([a-z])/g, (m, letter) => `Mc${letter.toUpperCase()}`)
}

export const formatDate = (date) => {
    if (!date) return ''
    if (typeof date === 'string') {
        const parsed = new Date(date)
        if (isNaN(parsed)) return date
        return /T\d/.test(date)
            ? `${pad(parsed.getMonth() + 1)}/${pad(parsed.getDate())}/${parsed.getFullYear()}`
            : `${pad(parsed.getUTCMonth() + 1)}/${pad(parsed.getUTCDate())}/${parsed.getUTCFullYear()}`
    }
    if (typeof date !== 'object') return ''
    const [Year, Month, Day] = [scalar(date.Year), scalar(date.Month), scalar(date.Day)]
    if (!Year) return ''
    if (Month && Day) return `${pad(Month)}/${pad(Day)}/${Year}`
    if (Month) return `${pad(Month)}/${Year}`
    return String(Year)
}

const formatMonthYear = (date) => {
    if (!date || typeof date !== 'object' || !scalar(date.Year)) return ''
    return scalar(date.Month) ? `${pad(date.Month)}/${date.Year}` : String(date.Year)
}

const dateValue = (date) => {
    if (!date) return 0
    if (typeof date === 'string') return new Date(date).getTime() || 0
    return date.Year ? new Date(date.Year, (date.Month || 1) - 1, date.Day || 1).getTime() : 0
}

const yearOf = (date) => (date && typeof date === 'object' ? date.Year : date ? new Date(date).getUTCFullYear() : null) || null

export const isWithinLastYear = (date, now = new Date()) => {
    const time = dateValue(date)
    if (!time) return false
    const yearAgo = new Date(now)
    yearAgo.setFullYear(now.getFullYear() - 1)
    return time >= yearAgo.getTime()
}

export const formatMoney = (value) => {
    if (!value && value !== 0) return ''
    const text = String(value)
    if (!/\d/.test(text)) return text
    const number = Number(text.replace(/[^0-9.-]/g, ''))
    if (isNaN(number)) return text
    return `${number < 0 ? '-' : ''}$${Math.abs(number).toLocaleString('en-US', { maximumFractionDigits: 0 })}`
}

export const formatEin = (value) => {
    const d = digits(value)
    return d.length === 9 ? `${d.slice(0, 2)}-${d.slice(2)}` : d
}

export const formatPhone = (value) => {
    const text = String(scalar(value))
    const d = text.replace(/[^\dXx]/g, '').toUpperCase()
    return /^\d{6}[\dX]{4}$/.test(d) ? `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}` : text
}

const ssnDigits = (value) => String(scalar(value)).replace(/[^0-9]/g, '')

export const compareSsn = (a, b) => {
    const [x, y] = [ssnDigits(a), ssnDigits(b)]
    if (x.length === 9 && y.length === 9) return x === y ? 'same' : 'different'
    if (x.length >= 5 && y.length >= 5) return x.slice(0, 5) === y.slice(0, 5) ? 'prefix' : 'different'
    return null
}

export const bestSsnMatch = (values, owner) => {
    const results = values.map(v => compareSsn(v, owner)).filter(Boolean)
    return ['same', 'prefix', 'different'].find(r => results.includes(r)) || null
}

export const maskedSsn = (value) => {
    const match = String(value || '').replace(/[-\s]/g, '').match(/^(\d{3})(\d{2})(?:[xX*]{4}|\d{4})$/)
    return match ? `${match[1]}-${match[2]}-xxxx` : ''
}

export const formatAddress = (address) => {
    if (!address || typeof address !== 'object') return ''
    const street = scalar(address.StreetAddress1) || [address.StreetNumber, address.StreetPreDirection, address.StreetName,
        address.StreetSuffix, address.StreetPostDirection].map(scalar).filter(Boolean).join(' ')
    const unit = [address.UnitDesignation, address.UnitNumber].map(scalar).filter(Boolean).join(' ')
    const stateZip = [address.State, address.Zip5].map(scalar).filter(Boolean).join(' ')
    return [titleCase([street, unit].filter(Boolean).join(' ')), titleCase(address.City), stateZip].filter(Boolean).join(', ')
}

const placeOf = (address) => [titleCase(address?.City), scalar(address?.State)].filter(Boolean).join(', ')

const personName = (name) => {
    if (!name) return ''
    if (typeof name === 'string') return titleCase(name)
    return titleCase(name.Full || [name.First, name.Middle, name.Last].filter(Boolean).join(' '))
}

const partyName = (party) => {
    if (!party) return ''
    const name = party.CompanyName || party.OriginalName || party.OriginName || party.Name
    return typeof name === 'object' ? personName(name) : titleCase(name)
}

const EXPERIAN_TITLES = {
    PRES: 'President', VP: 'Vice President', SEC: 'Secretary', TREAS: 'Treasurer', TRES: 'Treasurer', MGR: 'Manager', MBR: 'Member',
    OWNR: 'Owner', OWNER: 'Owner', DIR: 'Director', AGT: 'Agent', PTNR: 'Partner', 'UCC DEBTOR': 'UCC debtor', CEO: 'CEO', CFO: 'CFO', COO: 'COO',
    'OTHER LIST': 'Other', 'GEN MGR': 'General Manager', 'AUTH REP': 'Authorized Representative', INCORPORAT: 'Incorporator',
    'CUST SERV': 'Customer Service', COORDINATR: 'Coordinator', 'OPS MGR': 'Operations Manager', 'GEN PARTNR': 'General Partner',
    'ACCT MGR': 'Account Manager', 'MANG PART': 'Managing Partner', 'SEC-TREAS': 'Secretary-Treasurer', 'PLANT MGR': 'Plant Manager',
}
const experianTitle = (title) => {
    const key = String(title || '').trim().toUpperCase()
    return key ? EXPERIAN_TITLES[key] || titleCase(key) : ''
}
const firstLast = (name) => {
    const words = String(name || '').split(/\s+/).filter(Boolean)
    return words.length > 2 ? `${words[0]} ${words[words.length - 1]}` : words.join(' ')
}

const samePerson = (a, b) => {
    const words = (value) => String(value || '').toUpperCase().split(/[^A-Z0-9]+/).filter(Boolean).sort().join(' ')
    return Boolean(words(a)) && words(a) === words(b)
}

const sameName = (a, b) => String(a || '').toUpperCase().replace(/[^A-Z0-9]/g, '') === String(b || '').toUpperCase().replace(/[^A-Z0-9]/g, '')

const STATES = {
    AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas', CA: 'California', CO: 'Colorado', CT: 'Connecticut', DE: 'Delaware',
    DC: 'District of Columbia', FL: 'Florida', GA: 'Georgia', HI: 'Hawaii', ID: 'Idaho', IL: 'Illinois', IN: 'Indiana', IA: 'Iowa',
    KS: 'Kansas', KY: 'Kentucky', LA: 'Louisiana', ME: 'Maine', MD: 'Maryland', MA: 'Massachusetts', MI: 'Michigan', MN: 'Minnesota',
    MS: 'Mississippi', MO: 'Missouri', MT: 'Montana', NE: 'Nebraska', NV: 'Nevada', NH: 'New Hampshire', NJ: 'New Jersey',
    NM: 'New Mexico', NY: 'New York', NC: 'North Carolina', ND: 'North Dakota', OH: 'Ohio', OK: 'Oklahoma', OR: 'Oregon',
    PA: 'Pennsylvania', RI: 'Rhode Island', SC: 'South Carolina', SD: 'South Dakota', TN: 'Tennessee', TX: 'Texas', UT: 'Utah',
    VT: 'Vermont', VA: 'Virginia', WA: 'Washington', WV: 'West Virginia', WI: 'Wisconsin', WY: 'Wyoming', PR: 'Puerto Rico',
}

const entityKind = (businessType) => {
    const type = String(businessType || '').toUpperCase()
    if (!type) return ''
    if (/FICTITIOUS|ASSUMED|\bDBA\b|TRADE NAME/.test(type)) return 'assumed name'
    if (/LIMITED LIABILITY PARTNERSHIP|\bLLP\b/.test(type)) return 'LLP'
    if (/LIMITED LIABILITY|\bLLC\b|L\.L\.C/.test(type)) return 'LLC'
    if (/PARTNERSHIP|\bLP\b/.test(type)) return 'partnership'
    if (/PROPRIETOR/.test(type)) return 'sole proprietorship'
    if (/NON.?PROFIT/.test(type)) return 'nonprofit'
    if (/CORP|STOCK|\bBCA\b|\bINC\b|INCORPORAT|FOR.?PROFIT/.test(type)) return 'corporation'
    return titleCase(businessType)
}

const capitalize = (value) => (value ? value.charAt(0).toUpperCase() + value.slice(1) : '')

export const entityLabel = (businessType, foreignDomestic) => {
    const type = String(businessType || '').toUpperCase()
    const scope = foreignDomestic === 'F' || /FOREIGN|OUT OF STATE/.test(type) ? 'Foreign'
        : foreignDomestic === 'D' || /DOMESTIC|\(D\)/.test(type) ? 'Domestic' : ''
    const kind = entityKind(businessType)
    return scope ? [scope, kind].filter(Boolean).join(' ') : capitalize(kind)
}

const WARNING_EVENT = /DISSOL|REVOC|FORFEIT|SUSPEN|TERMINAT|CANCEL|DELINQUEN|INVOLUNTARY|NOT IN GOOD STANDING/
export const historyTone = (description) => {
    const text = String(description || '').toUpperCase()
    if (/REINSTAT|REVIV/.test(text)) return 'approved'
    return WARNING_EVENT.test(text) ? 'declined' : null
}

const GOOD_STATUSES = new Set(['ACTIVE', 'GOOD STANDING', 'IN GOOD STANDING', 'CURRENT'])
export const statusTone = (status) => {
    const value = String(status || '').trim().toUpperCase()
    if (!value || value === 'N/A' || value === 'NA' || value === 'UNKNOWN') return 'neutral'
    return GOOD_STATUSES.has(value) ? 'approved' : 'declined'
}

export const lienStatus = (filingTypes, releaseDate) => {
    const all = filingTypes.map(t => String(t || '').toUpperCase()).join(' | ')
    if (all.includes('FILED IN ERROR')) return 'Filed in error'
    if (all.includes('DISMISS')) return 'Dismissed'
    if (all.includes('VACAT')) return 'Vacated'
    if (all.includes('ERRONEOUS TERMINATION')) return 'Open'
    if (/RELEASE|RELEAS\b|SATISF|TERMINATION|WITHDRAW/.test(all) || dateValue(releaseDate)) return 'Released'
    return 'Open'
}

export const cleanLienType = (type) => {
    const text = String(type || '').toUpperCase()
    if (text.includes('FORCIBLE ENTRY')) return 'Eviction (forcible entry/detainer)'
    const label = titleCase(text.replace(/FILED IN ERROR-?/, '').replace(/\bST\b/, 'STATE').replace(/\bFED\b/, 'FEDERAL')
        .replace(/\b(RELEASE|RELEAS|SATISFACTION|DISMISSAL|WITHDRAWAL|VACATED)\b/g, '').replace(/\s+/g, ' ').trim())
    return label === 'Civil' ? 'Civil case' : label
}

const REGISTRATION_STATUS = {
    ACTIVE: 'approved', 'GOOD STANDING': 'approved', EXPIRED: 'declined', INCOMPLETE: 'watch', WITHDRAWAL: 'watch',
}
const registrationTone = (statusDecode) => REGISTRATION_STATUS[String(statusDecode || '').trim().toUpperCase()] || 'neutral'

const ASSOCIATE_LINKS = [
    [/^UCC$/, 'UCC filing', false], [/^MVR$/, 'vehicle records', false], [/^REAL PROP/, 'real property', false],
    [/^JUDGMENT/, 'lien or judgment', true], [/^BANKRUPTCY/, 'bankruptcy', true], [/^FORECLOSURE/, 'foreclosure', true],
    [/^NOD$/, 'notice of default', true],
]
const associateLinks = (role) => String(role || '').split(';').map(part => part.trim().toUpperCase()).filter(Boolean)
    .map(part => {
        const match = ASSOCIATE_LINKS.find(([pattern]) => pattern.test(part))
        return match ? { label: match[1], flag: match[2] } : { label: titleCase(part), flag: false }
    })

const RECEIVABLES = /RECEIVABLE|ACCOUNT\(S\)|ACCOUNTS ALL|ALL ACCOUNTS/
const ALL_ASSETS = /ALL ASSETS|ALL PERSONAL PROPERTY|ALL (OF )?(THE )?DEBTOR'?S? (ASSETS|PERSONAL PROPERTY)/
const JOIN_WORDS = /^(AND|OR|OF|THE|ALL|TO|IN|FOR|WITH|INCLUDING|NOW|ANY)$/i
const joinPieces = (pieces) => pieces.filter(piece => typeof piece === 'string' || typeof piece === 'number').map(String).reduce((text, piece) => {
    const lastWord = text.split(/[\s;]/).pop()
    const firstWord = piece.split(/[\s;]/)[0]
    return text + (text && (JOIN_WORDS.test(lastWord) || JOIN_WORDS.test(firstWord)) ? ' ' : '') + piece
}, '')

const COLLATERAL_KEYWORDS = [
    ['All assets', ALL_ASSETS], ['Receivables', RECEIVABLES], ['Inventory', /INVENTOR/], ['Equipment', /EQUIPMENT|MACHINERY/],
    ['Vehicles', /VEHICLE|TRACTOR|TRAILER|TRUCK|\bVIN\b/], ['Real estate', /REAL (ESTATE|PROPERTY)|FIXTURE/],
]

const BOILERPLATE = [
    /,?\s*(now owned or )?(hereafter|hereinafter) (acquired|arising)( or existing)?/g, /\s+(all\s+)?including (all )?proceeds( and products)?( thereof)?/g,
    /\s+and (all )?(the )?proceeds( and products)?( thereof)?/g, /\s+proceeds( and products)?( thereof)?$/g, /\s+of (the )?debtor/g, /\s+all$/g, /^all\s+(?!assets)/g,
]
const plainCollateral = (phrases) => {
    const items = []
    phrases.forEach(phrase => {
        let item = phrase.toLowerCase().replace(/\((s|es)\)/g, 's')
        BOILERPLATE.forEach(rx => { item = item.replace(rx, '') })
        item = item.replace(/\s+/g, ' ').replace(/^[,\s]+|[,\s]+$/g, '')
        if (item && !items.includes(item)) items.push(item)
    })
    const text = items.join(', ').replace(/\b(vin|ucc|llc|inc|usa|irs)\b/g, word => word.toUpperCase())
    return text ? text.charAt(0).toUpperCase() + text.slice(1) : ''
}

const collateralOf = (filings) => {
    const phrases = []
    filings.forEach(f => {
        const pieces = list(f?.Collaterals?.Collateral).slice().sort((x, y) => Number(x?.CollateralSequence || 0) - Number(y?.CollateralSequence || 0))
        joinPieces(pieces.map(c => c?.CollateralDescription || '')).split(';').forEach(part => {
            const phrase = part.trim().replace(/\s+/g, ' ')
            if (phrase && !phrases.some(existing => existing.toUpperCase() === phrase.toUpperCase())) phrases.push(phrase)
        })
    })
    return describeCollateral(phrases)
}

export const describeCollateral = (phrases) => {
    const text = phrases.join('; ')
    const upper = text.toUpperCase()
    return {
        text: text ? text.charAt(0).toUpperCase() + text.slice(1).toLowerCase() : '',
        plain: plainCollateral(phrases),
        allAssets: ALL_ASSETS.test(upper),
        receivables: RECEIVABLES.test(upper),
        summary: text ? (COLLATERAL_KEYWORDS.filter(([, rx]) => rx.test(upper)).map(([label]) => label).join(', ') || 'Other assets') : '',
    }
}

const CHANGE_LABELS = { AMENDMENT: 'Amended', CONTINUATION: 'Continued', TERMINATION: 'Terminated', ASSIGNMENT: 'Assigned' }
const latestChange = (filings) => {
    const dated = filings.filter(f => f?.FilingDate && !/^INITIAL/i.test(f?.FilingType || '')).sort((a, b) => dateValue(b.FilingDate) - dateValue(a.FilingDate))
    const latest = dated[0]
    if (!latest) return ''
    const type = String(latest.FilingType || '').toUpperCase()
    const label = CHANGE_LABELS[Object.keys(CHANGE_LABELS).find(key => type.startsWith(key))] || titleCase(latest.FilingType) || 'Changed'
    return `${label} ${formatDate(latest.FilingDate)}${dated.length > 1 ? ` · ${dated.length} changes` : ''}`
}

const partiesOf = (filings, group, key) => {
    const parties = []
    filings.forEach(f => list(f?.[group]?.[key]).forEach(p => {
        const name = partyName(p)
        if (name && !parties.some(existing => sameName(existing.name, name))) parties.push({ name, address: formatAddress(p?.Address), ssn: maskedSsn(p?.SSN) })
    }))
    return parties
}

const uccRow = (ucc) => {
    const filings = list(ucc?.Filings?.Filing)
    const secured = []
    filings.forEach(f => list(f?.Secureds?.Secured).forEach(s => {
        const name = partyName(s)
        if (name && !secured.some(existing => sameName(existing, name))) secured.push(name)
    }))
    const expiration = filings.map(f => f?.ExpirationDate).filter(Boolean).sort((a, b) => dateValue(b) - dateValue(a))[0]
    return {
        date: ucc?.OriginalFilingDate,
        dateLabel: formatDate(ucc?.OriginalFilingDate),
        recent: isWithinLastYear(ucc?.OriginalFilingDate),
        stateName: STATES[ucc?.FilingJurisdiction] || ucc?.FilingJurisdiction || '',
        type: titleCase(ucc?.LatestFilingType || filings[filings.length - 1]?.FilingType),
        state: ucc?.FilingJurisdiction || '',
        secured,
        expires: formatDate(expiration),
        collateral: collateralOf(filings),
        change: latestChange(filings),
        lenders: partiesOf(filings, 'Secureds', 'Secured'),
        assignees: partiesOf(filings, 'Assignees', 'Assignee'),
        debtors: partiesOf(filings, 'Debtors', 'Debtor'),
        office: [titleCase(ucc?.FilingAgencyName), formatAddress(ucc?.FilingAgencyAddress)].filter(Boolean).join(', '),
        terminated: /TERMINAT/i.test(ucc?.LatestFilingType || filings[filings.length - 1]?.FilingType || ''),
        number: ucc?.OriginalFilingNumber || filings[0]?.FilingNumber || '',
    }
}

const byDateDesc = (a, b) => dateValue(b.date) - dateValue(a.date)

const EXPERIAN_COLLATERAL = { 1: 'Receivables', 4: 'Equipment', 5: 'Furniture and fixtures', 6: 'Inventory', 9: 'Future property', Q: 'Vehicles', H: 'Real estate' }
const EXPERIAN_ACTIONS = { FILED: 'Filed', AMENDED: 'Amended', TERM: 'Terminated', CONT: 'Continued', ASSIGN: 'Assigned', RELEASE: 'Released', 'P-REL': 'Partly released' }
const experianAction = (action) => {
    const key = Object.keys(EXPERIAN_ACTIONS).find(k => String(action || '').toUpperCase().startsWith(k))
    return key ? EXPERIAN_ACTIONS[key] : titleCase(action)
}
const experianUccs = (experian, now = new Date()) => {
    const groups = new Map()
    list(experian?.UCCFilings?.UCCFiling).forEach(f => {
        const key = `${f?.FileState || ''}|${f?.FileNumber || f?.DocumentNumber || ''}`
        if (!groups.has(key)) groups.set(key, [])
        groups.get(key).push(f)
    })
    return [...groups.values()].map(records => {
        const dates = records.map(r => r?.DateFiled).filter(d => dateValue(d)).sort((a, b) => dateValue(a) - dateValue(b))
        const codes = [...new Set(records.flatMap(r => list(r?.Collaterals?.Collateral)).map(c => String(c?.Code ?? '').toUpperCase())
            .filter(code => code && code !== '0'))]
        const descriptions = records.flatMap(r => list(r?.Collaterals?.Collateral)).filter(c => String(c?.Code) !== '0')
        const collateral = [...new Set(codes.map(code => EXPERIAN_COLLATERAL[code]
            || titleCase(descriptions.find(c => String(c?.Code).toUpperCase() === code)?.Description)).filter(Boolean))]
        const actions = [...new Set(records.map(r => experianAction(r?.Action)).filter(Boolean))]
        const continuations = records.filter(r => experianAction(r?.Action) === 'Continued').length
        const lapsesAt = dateValue(dates[0]) ? new Date(dateValue(dates[0])).setFullYear(new Date(dateValue(dates[0])).getFullYear() + 5 * (1 + continuations)) : 0
        const lapsed = Boolean(lapsesAt && lapsesAt < now.getTime() && !actions.includes('Terminated') && !actions.includes('Released'))
        if (lapsed) actions.push('Lapsed')
        return {
            date: dates[0],
            dateLabel: formatDate(dates[0]),
            recent: isWithinLastYear(dates[0], now),
            state: titleCase(records[0]?.FileState),
            office: titleCase(records[0]?.FilingLocation).replace(/\b([A-Za-z]{2})$/, code => (STATES[code.toUpperCase()] ? code.toUpperCase() : code)),
            number: records[0]?.FileNumber || '',
            documents: [...new Set(records.map(r => r?.DocumentNumber).filter(Boolean))],
            actions,
            ended: actions.includes('Terminated') || actions.includes('Released') || lapsed,
            lapsed,
            collateral,
            receivables: codes.includes('1'),
            lender: records.map(r => r?.SecuredParty).find(Boolean) ? titleCase(records.map(r => r?.SecuredParty).find(Boolean)) : '',
            assignee: records.map(r => r?.Assignee).find(Boolean) ? titleCase(records.map(r => r?.Assignee).find(Boolean)) : '',
        }
    }).sort(byDateDesc)
}

const numberOr = (value) => (value === null || value === undefined || value === '' || Number.isNaN(Number(value)) ? null : Number(value))
export const dbtTone = (days) => (days == null ? null : days >= 16 ? 'declined' : days >= 6 ? 'watch' : 'approved')
const fullYear = (year) => { const y = numberOr(year); return y == null ? '' : y < 100 ? 2000 + y : y }
const AGING = [['Current', 'On time'], ['Day_01_30', '1-30 days late'], ['Day_31_60', '31-60 days'], ['Day_61_90', '61-90 days'], ['Day_91_Plus', '91+ days']]

const countOf = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`

const lateShare = (dbt) => {
    const values = AGING.map(([key]) => numberOr(dbt?.[key]) || 0)
    return values.some(Boolean) ? 100 - values[0] : null
}
const agingOf = (dbt) => AGING.map(([key, label]) => ({ label, percent: numberOr(dbt?.[key]) || 0 }))

const INQUIRY_TYPES = {
    'FINCL SVCS': 'Financial services', PERSNLSVCS: 'Personal services', FACTOR: 'Factoring', 'ADV MEDIA': 'Advertising and media',
    'EQUIP LEAS': 'Equipment leasing', 'CRED CARD': 'Credit cards', 'COMMUN SVC': 'Communication services', TRANSPORTN: 'Transportation',
    'TRUCK DLR': 'Truck dealers', 'BLDG MATRL': 'Building materials', PERSCOMPTR: 'Personal computers', 'FUEL DISTR': 'Fuel distributors',
}

const paymentHistory = (experian, now) => {
    const summary = list(experian?.ExecutiveSummaries?.ExecutiveSummary)[0] || {}
    const totals = list(experian?.TradePaymentTotals?.TradePaymentTotal)[0] || {}
    const combined = totals.CombinedTrades || {}
    const trades = numberOr(combined.TradeCount)
    const days = numberOr(summary.CurrentDaysBeyondTerms) ?? numberOr(combined.Debt)
    const aging = AGING.map(([key, label]) => ({ label, percent: numberOr(combined.DBTPercentages?.[key]) || 0 }))
    const months = list(experian?.TradePaymentTrends?.PaymentTrend).map(m => ({
        label: formatMonthYear({ Month: m?.Date?.Month, Year: fullYear(m?.Date?.Year) }),
        sort: fullYear(m?.Date?.Year) * 12 + (numberOr(m?.Date?.Month) || 0),
        days: numberOr(m?.Debt), balance: numberOr(m?.AccountBalance), late: lateShare(m?.DBTPercentages),
    }))
    const quarters = list(experian?.TradeQuarterlyAverages?.QuarterlyAverage).map(q => {
        const quarter = numberOr(q?.Quarter)
        return {
            label: [quarter >= 1 && quarter <= 4 ? `Q${quarter}` : '', fullYear(q?.Year)].filter(Boolean).join(' '),
            sort: fullYear(q?.Year) * 12 + (quarter || 0) * 3,
            days: numberOr(q?.Debt), balance: numberOr(q?.AccountBalance), late: lateShare(q?.DBTPercentages),
        }
    })
    const history = (months.length ? months : quarters).sort((a, b) => b.sort - a.sort)

    const accounts = list(experian?.TradePaymentDetails?.PaymentDetail).map(a => {
        const accountAging = agingOf(a?.DBTPercentages)
        const late = lateShare(a?.DBTPercentages)
        const seriouslyLate = accountAging.slice(2).some(x => x.percent > 0)
        const balance = numberOr(a?.MaskedAccountBalance)
        const highCredit = numberOr(a?.RecentHighCredit)
        return {
            category: titleCase(a?.BusinessCategory) || 'Supplier',
            type: titleCase(a?.TradeTypeDesc),
            terms: titleCase(String(a?.PaymentTerms || '').trim()),
            balance: balance != null ? formatMoney(balance) : a?.AccountBalanceMask || '',
            highCredit: highCredit != null ? formatMoney(highCredit) : a?.HighCreditMask || '',
            aging: accountAging.some(x => x.percent) ? accountAging : [],
            late,
            tone: seriouslyLate ? 'declined' : late ? 'watch' : late === 0 ? 'approved' : null,
            reported: formatMonthYear(a?.ReportedDate),
            lastActivity: formatMonthYear(a?.ActivityDate),
            comments: capitalize(String(a?.Comments || '').toLowerCase().trim()),
            isNew: /^(Y|YES|TRUE|1)$/i.test(String(a?.NewTradeFlag || '')),
            sort: dateValue(a?.ReportedDate),
        }
    }).sort((a, b) => (b.late || 0) - (a.late || 0) || b.sort - a.sort)

    const inquiryTypes = new Map()
    let inquiryTotal = 0
    let inquiryRecent = 0
    const ranges = []
    list(experian?.InquiryRecords?.InquiryRecord).forEach(rec => {
        ranges.push([rec?.DateRange?.StartDate, rec?.DateRange?.EndDate])
        list(rec?.Inquiries?.Inquiry).forEach(inq => {
            const raw = String(inq?.InquiryType || '').trim().toUpperCase()
            if (raw === 'TOTAL') return
            const type = INQUIRY_TYPES[raw] || titleCase(raw) || 'Other'
            list(inq?.InquiryCounts?.InquiryCount).forEach(c => {
                const count = numberOr(c?.Count) || 0
                inquiryTotal += count
                inquiryTypes.set(type, (inquiryTypes.get(type) || 0) + count)
                if (dateValue(c?.Date) && now.getTime() - dateValue(c.Date) < 183 * 86400000) inquiryRecent += count
            })
        })
    })
    const starts = ranges.map(([start]) => start).filter(d => dateValue(d)).sort((a, b) => dateValue(a) - dateValue(b))
    const ends = ranges.map(([, end]) => end).filter(d => dateValue(d)).sort((a, b) => dateValue(b) - dateValue(a))
    const inquiries = inquiryTotal > 0 ? {
        total: inquiryTotal, recent: inquiryRecent,
        period: [formatMonthYear(starts[0]), formatMonthYear(ends[0])].filter(Boolean).join(' to '),
        byType: [...inquiryTypes.entries()].sort((a, b) => b[1] - a[1]).map(([type, count]) => ({ type, count })),
    } : null

    const regular = totals.RegularTrades || {}
    const fresh = totals.NewTrades || {}
    const money = (value) => (numberOr(value) != null ? formatMoney(value) : '')
    const accountsLine = (group) => [numberOr(group.TradeCount) != null && countOf(Number(group.TradeCount), 'account'), money(group.MaskedAccountBalance),
        numberOr(group.Debt) != null && `${group.Debt} days late`, lateShare(group.DBTPercentages) != null && `${lateShare(group.DBTPercentages)}% paid late`].filter(Boolean).join(' · ')
    const summaryDetails = [
        ['Balance range (6 months)', [money(summary.LowBalance), money(summary.HighBalance)].filter(Boolean).join(' to ')],
        ['Median credit extended', money(summary.MedianOfCreditExtended) || money(totals.HighestCreditMedian)],
        ['All-industry average', numberOr(summary.AverageAllIndustryDBT) != null ? countOf(Number(summary.AverageAllIndustryDBT), 'day') + ' late' : ''],
        ['Industry compared with', titleCase(summary.IndustryDescription)],
        ['Prediction date', formatDate(summary.DateOfPrediction)],
        ['Regular accounts', accountsLine(regular)],
        ['New accounts', accountsLine(fresh)],
        ['Accounts with aging data', numberOr(totals.AgedTradesCount) != null ? String(totals.AgedTradesCount) : ''],
    ].filter(([, value]) => value).map(([label, value]) => ({ label, value }))

    if (days == null && !trades && !history.length && !accounts.length && !inquiries) return null
    const balance = numberOr(summary.CurrentBalance) ?? numberOr(totals.AccountBalanceCombined)
    const highCredit = numberOr(summary.HighestCreditExtended) ?? numberOr(combined.RecentHighCredit)
    const performance = String(summary.PaymentPerformance || '')
    return {
        days,
        tone: dbtTone(days),
        predicted: numberOr(summary.PredictedDaysBeyondTerms),
        industry: numberOr(summary.AverageIndustryDBT),
        trend: summary.PaymentTrend || '',
        worsening: /LATE|WORSE|DETERIORAT/i.test(summary.PaymentTrend || ''),
        performance: /than \d+%$/.test(performance) ? `${performance} of similar businesses` : performance,
        trades,
        balance: balance != null ? formatMoney(balance) : '',
        highCredit: highCredit != null ? formatMoney(highCredit) : '',
        aging: aging.some(a => a.percent) ? aging : [],
        history,
        historyByMonth: months.length > 0,
        accounts,
        lateAccounts: accounts.filter(a => a.late).length,
        inquiries,
        summaryDetails,
    }
}

const RESOLVED_ACTION = /\bREL\b|RELEAS|\bSAT\b|SATISF|DISCH|DISM|VACAT|WITHDRAW|CLOSED|TERMINAT/i
const RECORD_ACTIONS = { LN: 'Filed', LIEN: 'Filed', REL: 'Released', SAT: 'Satisfied', FILED: 'Filed' }
const recordAction = (action) => {
    const raw = String(action || '').trim().toUpperCase()
    const chapter = raw.match(/^(?:CHAP)?(\d+)(?:-(DISCH|DISM))?$/)
    if (chapter) return `Chapter ${chapter[1]}${chapter[2] === 'DISCH' ? ' discharged' : chapter[2] === 'DISM' ? ' dismissed' : ' filed'}`
    return RECORD_ACTIONS[raw] || titleCase(raw)
}
const RECORD_TYPES = { 'STAT-TX-': 'State tax lien', 'FED-TX-': 'Federal tax lien', 'CNTY-TX-': 'County tax lien', 'JUDG-': 'Judgment', 'BK-': 'Bankruptcy',
    'STATE TAX': 'State tax lien', 'FEDERAL TA': 'Federal tax lien' }
const experianRecords = (experian) => {
    const raw = [
        ...list(experian?.TaxLiens?.TaxLien).map(x => ({ kind: 'Tax lien', ...x })),
        ...list(experian?.Judgments?.Judgment).map(x => ({ kind: 'Judgment', ...x })),
        ...list(experian?.Bankruptcies?.Bankruptcy).map(x => ({ kind: 'Bankruptcy', ...x })),
    ]
    const resolved = x => RESOLVED_ACTION.test(String(x.Action || ''))
    const releaseOf = new Map()
    const folded = new Set()
    raw.filter(x => x.kind !== 'Bankruptcy' && resolved(x)).forEach(release => {
        const candidates = raw.filter(f => f.kind === release.kind && !resolved(f) && !releaseOf.has(f))
        const filing = candidates.find(f => release.DocumentNumber && f.DocumentNumber === release.DocumentNumber)
            || candidates.find(f => release.FilingLocation && f.FilingLocation === release.FilingLocation
                && numberOr(f.LiabilityAmount) === numberOr(release.LiabilityAmount))
        if (!filing) return
        releaseOf.set(filing, release)
        folded.add(release)
    })
    return raw.filter(x => !folded.has(x)).map(x => {
        const release = releaseOf.get(x)
        return {
            kind: x.kind,
            type: RECORD_TYPES[String(x.Type || '').trim().toUpperCase()] || titleCase(x.Type),
            action: release ? recordAction(release.Action) : recordAction(x.Action),
            open: !release && !resolved(x),
            date: x.DateFiled,
            dateLabel: formatDate(x.DateFiled),
            releasedLabel: release ? formatDate(release.DateFiled) : '',
            amount: numberOr(x.LiabilityAmount) > 0 ? formatMoney(x.LiabilityAmount) : '',
            amountValue: numberOr(x.LiabilityAmount),
            where: titleCase(x.FilingLocation),
            creditor: titleCase(x.CreditorName),
            description: capitalize(String(x.Description || '').toLowerCase().trim()),
            number: x.DocumentNumber || '',
            assets: numberOr(x.AssetAmount) > 0 ? formatMoney(x.AssetAmount) : '',
            disputed: /^(Y|YES|TRUE|1)$/i.test(String(x.DisputeIndicator || release?.DisputeIndicator || '')),
        }
    }).sort((a, b) => dateValue(b.date) - dateValue(a.date))
}

const markRenewed = (rows, keyOf, dateOf) => {
    rows.forEach(row => {
        if (!row.expired) return
        const newer = rows.some(other => other !== row && keyOf(other) === keyOf(row)
            && ((other.expiryValue || 0) > (row.expiryValue || 0) || dateOf(other) > dateOf(row)))
        if (newer) Object.assign(row, { expired: false, renewed: true })
    })
}

const PLAN_FEATURES = {
    '1A': 'defined benefit', '1B': 'defined benefit (pension portion)', '1C': 'floor-offset', '1D': 'cash balance', '1E': 'defined benefit with lump sums',
    '2A': 'age or service weighted', '2B': 'target benefit', '2C': 'money purchase', '2D': 'offset', '2E': 'profit-sharing', '2F': 'ERISA 404(c)',
    '2G': 'participant-directed', '2H': 'partly participant-directed', '2I': 'stock bonus', '2J': '401(k)', '2K': 'participant-directed brokerage',
    '2L': '403(b)(1)', '2M': '403(b)(7)', '2R': 'participant-directed, no default', '2S': 'automatic enrollment', '2T': 'default investment',
    '3B': 'covers self-employed', '3C': 'not intended to be qualified', '3D': 'pre-approved plan', '3H': 'sponsor received services',
    '4A': 'health', '4B': 'life insurance', '4D': 'dental', '4E': 'vision', '4F': 'temporary disability', '4H': 'prepaid legal', '4L': 'death benefits',
    '4Q': 'other welfare', '4S': 'long-term disability',
}
const planTypes = (p) => [...new Set(`${p.TypePensionBnftCode || ''}${p.TypeWelfareBnftCode || ''}`.toUpperCase().match(/\d[A-Z]/g) || [])]
    .map(code => PLAN_FEATURES[code] || code).join(', ')
const FUNDING = { 1: 'Insurance', 2: 'Insurance contracts (412(e)(3))', 3: 'Trust', 4: 'General assets of the sponsor' }
const PLAN_ENTITY = { 1: 'Single employer', 2: 'Multiemployer', 3: 'Multiple employer', 4: 'Direct filing entity' }
const SCHEDULES = [['A', 'insurance'], ['C', 'service providers'], ['D', 'participating plans'], ['E', 'ESOP'], ['G', 'financial transactions'],
    ['H', 'financial statements'], ['I', 'financial statements (small plan)'], ['R', 'retirement plan details'], ['Ssa', 'separated participants'],
    ['P', 'trust'], ['T', 'coverage']]

function prune(value) {
    if (Array.isArray(value)) {
        const items = value.map(prune).filter(v => v !== undefined)
        return items.length ? items : undefined
    }
    if (!value || typeof value !== 'object') return value == null ? undefined : value
    const out = {}
    for (const [key, child] of Object.entries(value)) {
        const kept = prune(child)
        if (kept !== undefined) out[key] = kept
    }
    return Object.keys(out).length ? out : undefined
}

export function buildBusinessReport(record, applicationEin, now = new Date()) {
    const r = (record && typeof record === 'object' && !Array.isArray(record) && prune(record)) || {}
    const best = r.BestSection || {}
    const companyName = titleCase(best.CompanyName)
    const experianReport = list(r.ExperianBusinessReportSection?.ExperianBusinessReports?.ExperianBusinessReport)[0]

    const years = best.YearsInBusiness != null && best.YearsInBusiness !== '' && !isNaN(Number(best.YearsInBusiness)) ? Number(best.YearsInBusiness) : null
    const yearStarted = best.YearStarted || (years != null ? now.getFullYear() - years : null)
    const isNew = years === 0
    const ageLabel = isNew ? 'New business (less than 1 year)'
        : years != null ? `In business ${years} ${years === 1 ? 'year' : 'years'}${yearStarted ? ` (since ${yearStarted})` : ''}`
            : yearStarted ? `Started ${yearStarted}` : ''

    const corpFilings = list(r.IncorporationSection?.CorpFilings?.CorpFiling)
    const homeFiling = corpFilings.find(f => f.Origin && f.Origin === best.Address?.State) || corpFilings[0]
    const incorporation = homeFiling ? {
        label: [STATES[homeFiling.Origin] || homeFiling.Origin, entityKind(homeFiling.BusinessType)].filter(Boolean).join(' '),
        status: statusTone(homeFiling.BusinessStatus) === 'neutral' ? 'status not reported by state' : titleCase(homeFiling.BusinessStatus),
        tone: statusTone(homeFiling.BusinessStatus),
    } : null
    const badFilings = corpFilings.filter(f => statusTone(f.BusinessStatus) === 'declined')

    const formedAt = dateValue(homeFiling?.FilingDate)
    const ageMonths = formedAt && formedAt <= now.getTime()
        ? (now.getFullYear() - new Date(formedAt).getFullYear()) * 12 + now.getMonth() - new Date(formedAt).getMonth()
        : null
    const limitedHistory = years != null ? years < 2 && (ageMonths == null || ageMonths < 24) : ageMonths != null && ageMonths < 24
    const historyAge = !limitedHistory ? ''
        : ageMonths != null && (years == null || Math.floor(ageMonths / 12) === years)
            ? ageMonths < 1 ? 'less than a month old' : `${ageMonths} ${ageMonths === 1 ? 'month' : 'months'} old`
            : years === 0 ? 'less than a year old' : 'less than 2 years old'

    const industryRecord = list(r.IndustrySection?.IndustryRecords?.IndustryRecord)[0]
    const industry = list(industryRecord?.NAICSs?.NAICS)[0]?.NAICSDescription
        || titleCase(industryRecord?.IndustryDescription)
        || titleCase(list(industryRecord?.SICs?.SIC)[0]?.SICCodeDescription) || ''

    const reportEin = digits(best.Tin)
    const appEin = digits(applicationEin)
    const ein = {
        value: reportEin ? formatEin(reportEin) : '',
        applicationValue: appEin ? formatEin(appEin) : '',
        mismatch: Boolean(reportEin && appEin && reportEin !== appEin),
        others: list(best.OtherCompanyTins?.OtherCompanyTin)
            .map(o => ({ digits: digits(o?.Tin), names: list(o?.CompanyNames?.CompanyName).map(partyName).filter(Boolean) }))
            .filter((o, i, all) => o.digits.length === 9 && o.digits !== reportEin && all.findIndex(x => x.digits === o.digits) === i)
            .map(o => ({ value: formatEin(o.digits), name: o.names[0] || '', matchesApplication: o.digits === appEin })),
    }
    ein.applicationLinked = ein.mismatch && ein.others.some(o => o.matchesApplication)

    const website = best.URL || list(r.URLSection?.URLs?.URL)[0]?.URL || list(r.URLSection?.URLs?.URL)[0] || ''
    const aliases = list(best.OtherCompanyNames?.OtherCompanyName)
        .filter(o => o?.CompanyName && !sameName(o.CompanyName, best.CompanyName))
        .sort((a, b) => dateValue(b.DateLastSeen) - dateValue(a.DateLastSeen))
        .filter((o, i, all) => all.findIndex(x => sameName(x.CompanyName, o.CompanyName)) === i)
        .map(o => ({ name: titleCase(o.CompanyName), seen: [...new Set([formatMonthYear(o.DateFirstSeen), formatMonthYear(o.DateLastSeen)].filter(Boolean))].join('–') }))
    const industryDescription = capitalize(String(industryRecord?.BusinessDescription || '').toLowerCase().trim())
    const summary = {
        name: companyName,
        address: formatAddress(best.Address),
        phone: formatPhone(best.PhoneInfo?.Phone10),
        phoneDisconnected: best.Disconnected === true || /^(Y|YES|TRUE)$/i.test(String(best.Disconnected || '')),
        website: typeof website === 'string' ? website.toLowerCase() : '',
        ageLabel,
        isNew,
        incorporation,
        notActive: best.IsActive === false,
        industry,
        industryDescription: industryDescription && industryDescription.toUpperCase() !== String(industry).toUpperCase() ? industryDescription : '',
        aliases,
        ein,
    }

    const activeUccs = list(r.UCCSection?.AsDebtor?.ActiveUCCs?.ActiveUCC).map(uccRow).sort(byDateDesc)
    const endedUccs = list(r.UCCSection?.AsDebtor?.TerminatedUCCs?.TerminatedUCC).map(uccRow).sort(byDateDesc)
    const securedParties = new Set(activeUccs.flatMap(u => u.secured.map(s => s.toUpperCase())))
    const recentUccs = activeUccs.filter(u => u.recent).length
    const receivablesUccs = activeUccs.filter(u => u.collateral.receivables || u.collateral.allAssets).length
    const asLender = list(r.UCCSection?.AsSecured?.ActiveUCCs?.ActiveUCC).map(uccRow).sort(byDateDesc)
    const asLenderEnded = list(r.UCCSection?.AsSecured?.TerminatedUCCs?.TerminatedUCC).map(uccRow).sort(byDateDesc)
    const experianFilings = experianUccs(experianReport, now)
    const experianCollateral = list(experianReport?.CollateralAccounts?.Account).map(a => ({
        filings: Number(a?.TotalUCCFiled) || 0,
        kinds: [...new Set(list(a?.Collaterals?.Collateral).map(c => (EXPERIAN_COLLATERAL[String(c?.Code || '').trim()]
            || String(c?.Description || '').trim()).toLowerCase()).filter(Boolean))],
    })).find(a => a.kinds.length || a.filings) || null

    const knownBusinesses = [
        ...list(r.ConnectedBusinessSection?.ConnectedBusinessRecords?.ConnectedBusinessRecord),
        ...list(r.AssociateSection?.BusinessAssociates?.BusinessAssociate),
    ]
    const businessNameById = (id) => {
        const match = knownBusinesses.find(c => Object.values(c?.BusinessIds || {}).some(v => String(v) === String(id)))
        return match ? titleCase(match.CompanyName) : ''
    }

    const rolePeriod = (from, to, current) => {
        const start = formatMonthYear(from)
        const end = formatMonthYear(to)
        if (current) return start ? `since ${start}` : ''
        return [...new Set([start, end].filter(Boolean))].join('–')
    }
    const roleOf = (pos, current) => ({
        title: titleCase(pos?.CompanyTitle), period: rolePeriod(pos?.FromDate, pos?.ToDate, current), current,
    })
    const addRoles = (roles, extra, mainTitle) => {
        extra.forEach(role => {
            if (role.title === mainTitle && !role.period) return
            if (!roles.some(r => r.title === role.title && r.period === role.period)) roles.push(role)
        })
        return roles
    }

    const people = []
    const addPeople = (individuals, current) => list(individuals).forEach(p => {
        const name = personName(p?.Name)
        if (!name) return
        const from = p?.BestPosition?.FromDate
        const to = p?.BestPosition?.ToDate
        const id = p.UniqueId && String(p.UniqueId) !== '0' ? String(p.UniqueId) : null
        const title = titleCase(p?.BestPosition?.CompanyTitle)
        const otherRoles = [
            ...list(p?.OtherCurrents?.Position).map(pos => roleOf(pos, true)),
            ...list(p?.AllOthers?.Position).map(pos => roleOf(pos, false)),
        ].filter(role => role.title || role.period)
        const elsewhereId = Number(p.ExecutiveElsewhere) > 0 ? p.ExecutiveElsewhere : null
        const existing = people.find(x => (id && x.id === id) || samePerson(x.name, name))
        if (existing) {
            existing.current = existing.current || current
            existing.hasRecords = existing.hasRecords || Boolean(p.HasDerog)
            existing.deceased = existing.deceased || Boolean(p.IsDeceased)
            addRoles(existing.otherRoles, [...otherRoles, ...(title && title !== existing.title ? [{ title, period: rolePeriod(from, to, current), current }] : [])], existing.title)
            existing.elsewhereId = existing.elsewhereId || elsewhereId
            existing.address = existing.address || formatAddress(p?.Address)
            existing.ssn = existing.ssn || maskedSsn(p?.SSN)
            if (dateValue(from) && (!dateValue(existing.from) || dateValue(from) < dateValue(existing.from))) existing.from = from
            if (dateValue(to) > dateValue(existing.to)) existing.to = to
            return
        }
        people.push({ id, name, title, current, hasRecords: Boolean(p.HasDerog), deceased: Boolean(p.IsDeceased), from, to, address: formatAddress(p?.Address), ssn: maskedSsn(p?.SSN),
            otherRoles: addRoles([], otherRoles, title), elsewhereId })
    })
    const contact = r.ContactSection || {}
    addPeople(contact.CurrentExecutives?.Individual, true)
    addPeople(contact.CurrentIndividuals?.Individual, true)
    addPeople(contact.PriorExecutives?.Individual, false)
    addPeople(contact.PriorIndividuals?.Individual, false)
    people.forEach(p => {
        p.elsewhere = p.elsewhereId ? businessNameById(p.elsewhereId) || 'another business' : ''
        const from = formatMonthYear(p.from)
        const to = formatMonthYear(p.to)
        p.period = p.current ? (from ? `since ${from}` : '') : [...new Set([from, to].filter(Boolean))].join('–')
        if (!p.current && !from && to) p.period = `until ${to}`
    })
    people.sort((a, b) => (b.current - a.current) || (b.hasRecords - a.hasRecords))
    const flaggedPeople = people.filter(p => p.hasRecords).length
    const officers = []
    list(experianReport?.Demographics5610?.Demographic).forEach(d => {
        const name = personName({ First: d?.OfficerName?.First, Last: d?.OfficerName?.Last })
        if (!name) return
        const title = experianTitle(d?.OfficerTitle)
        const ssn = maskedSsn(d?.SSN)
        const existing = officers.find(o => samePerson(o.name, name))
        if (existing) {
            if (title && !existing.titles.includes(title)) existing.titles.push(title)
            existing.ssn = existing.ssn || ssn
            return
        }
        officers.push({ name, titles: title ? [title] : [], ssn, inLexis: people.some(p => samePerson(firstLast(p.name), name)) })
    })

    const linkedPeople = list(r.AssociateSection?.PersonAssociates?.PersonAssociate).map(a => {
        const links = associateLinks(a?.Role)
        const from = formatMonthYear(a?.FromDate)
        const to = formatMonthYear(a?.ToDate)
        return {
            name: personName(a?.Name), id: a?.UniqueId && String(a.UniqueId) !== '0' ? String(a.UniqueId) : null,
            via: capitalize(links.map(l => l.label).join(', ')), hasRecords: Boolean(a?.HasDerog), deceased: Boolean(a?.IsDeceased),
            period: [...new Set([from, to].filter(Boolean))].join('–'), address: formatAddress(a?.Address), ssn: maskedSsn(a?.SSN),
        }
    }).filter(a => a.name && !people.some(p => (a.id && p.id === a.id) || samePerson(p.name, a.name)))
        .sort((a, b) => b.hasRecords - a.hasRecords)
    const deceasedCurrent = people.filter(p => p.deceased && p.current).length

    const showFilingNames = corpFilings.some(f => f.CorporationName && !sameName(f.CorporationName, best.CompanyName))
    const filings = corpFilings.map(f => {
        const history = list(f.CorpHistories?.CorpHistory)
            .map(h => ({ date: h.Date, dateLabel: formatDate(h.Date), description: titleCase(h.Description), tone: historyTone(h.Description) }))
            .filter(h => h.description)
            .sort(byDateDesc)
        const lastWarning = history.find(h => h.tone === 'declined')
        const lastReinstatement = history.find(h => h.tone === 'approved')
        const historyWarning = Boolean(lastWarning && (!lastReinstatement || dateValue(lastWarning.date) > dateValue(lastReinstatement.date)))
        const standing = f.InGoodStanding
        const goodStanding = standing === true || /^(Y|YES|TRUE)$/i.test(String(standing ?? '')) ? true
            : standing === false || /^(N|NO|FALSE)$/i.test(String(standing ?? '')) ? false : null
        const termExpires = f.TermOfExistence?.ExpirationDate
        const termExpired = Boolean(dateValue(termExpires) && dateValue(termExpires) < now.getTime())
        const agentName = typeof f.RegisteredAgentName === 'object' ? titleCase(f.RegisteredAgentName?.CompanyName) || personName(f.RegisteredAgentName) : titleCase(f.RegisteredAgentName)
        return {
            name: titleCase(f.CorporationName),
            status: statusTone(f.BusinessStatus) === 'neutral' ? 'Status not reported by state' : titleCase(f.BusinessStatus),
            tone: statusTone(f.BusinessStatus),
            state: f.Origin || '',
            entity: entityLabel(f.BusinessType, f.ForeignDomesticIndicator),
            number: f.FilingNumber || '',
            date: f.FilingDate,
            dateLabel: formatDate(f.FilingDate),
            history,
            openWarning: historyWarning || goodStanding === false || termExpired,
            historyWarning,
            stateName: STATES[f.Origin] || f.Origin || '',
            dateType: titleCase(f.FilingType),
            goodStanding,
            agent: agentName ? { name: agentName, address: formatAddress(f.RegisteredAgentAddress) } : null,
            term: f.TermOfExistence?.Type || termExpires ? {
                label: [titleCase(f.TermOfExistence?.Type), Number(f.TermOfExistence?.Years) > 0 && `${f.TermOfExistence.Years} years`,
                    termExpires && `${termExpired ? 'expired' : 'expires'} ${formatDate(termExpires)}`].filter(Boolean).join(', '),
                expired: termExpired,
            } : null,
            nameType: titleCase(f.NameType),
            nameStatus: titleCase(f.NameStatus),
            purpose: capitalize(String(f.Purpose || '').toLowerCase().trim()),
            additionalInfo: capitalize(String(f.AdditionalInfo || '').toLowerCase().trim()),
            forProfit: /^Y/i.test(f.ForProfitIndicator || '') ? 'Yes' : /^N/i.test(f.ForProfitIndicator || '') ? 'No (nonprofit)' : '',
            foreign: f.ForeignState || f.ForeignIncorporationDate
                ? [STATES[f.ForeignState] || f.ForeignState, f.ForeignIncorporationDate && `formed ${formatDate(f.ForeignIncorporationDate)}`].filter(Boolean).join(', ') : '',
            stateOfOrigin: f.StateOfOrigin && f.StateOfOrigin !== f.Origin ? STATES[f.StateOfOrigin] || f.StateOfOrigin : '',
            addresses: list(f.Addresses?.Address).map(a => ({ type: titleCase(a?.AddressType), address: formatAddress(a?.Address) })).filter(a => a.address),
        }
    }).sort(byDateDesc)
    const agents = list(r.RegisteredAgentSection?.RegisteredAgents?.RegisteredAgent).map(a => {
        const from = formatMonthYear(a?.FromDate)
        const to = formatMonthYear(a?.ToDate)
        return {
            name: titleCase(a?.CompanyName) || personName(a?.Name), title: titleCase(a?.Title), address: formatAddress(a?.Address),
            period: [...new Set([from, to].filter(Boolean))].join('–'), to: a?.ToDate,
        }
    }).filter(a => a.name).sort((a, b) => dateValue(b.to) - dateValue(a.to))
    const filingsWithOpenWarnings = filings.filter(f => f.openWarning).length

    const registrations = list(r.BusinessRegistrationSection?.BusinessRegistrations?.BusinessRegistration)
        .map(g => {
            const expired = Boolean(dateValue(g.ExpirationDate) && dateValue(g.ExpirationDate) < now.getTime())
            const type = titleCase(g.CorpCodeDecode || g.FilingCodDecode) || 'Registration'
            const otherName = g.CompanyName && !sameName(g.CompanyName, best.CompanyName) ? titleCase(g.CompanyName) : ''
            return {
                type,
                sos: /SECRETARY OF STATE/i.test(type),
                otherName,
                name: titleCase(g.CompanyName),
                description: capitalize(String(g.Description || '').toLowerCase().trim()),
                reportedStatus: g.StatusDecode ? titleCase(g.StatusDecode) : g.Status ? `Status code ${g.Status}` : 'Not specified',
                status: expired ? 'Expired' : g.StatusDecode ? titleCase(g.StatusDecode) : g.Status ? `Status code ${g.Status}` : 'Not specified',
                tone: expired ? 'declined' : registrationTone(g.StatusDecode),
                state: g.Address?.State || '',
                stateName: STATES[g.Address?.State] || g.Address?.State || '',
                address: formatAddress(g.Address),
                phone: formatPhone(g.CompanyPhone10),
                entity: titleCase(g.SOSCodeDecode),
                filingKind: titleCase(g.FilingCodDecode),
                number: g.FilingNumber || '',
                date: g.FileDate || g.RecordDate,
                dateLabel: formatDate(g.FileDate || g.RecordDate),
                recordDate: formatDate(g.RecordDate),
                processDate: formatDate(g.ProccessDate),
                expires: formatDate(g.ExpirationDate),
                expired,
                expiryValue: dateValue(g.ExpirationDate),
            }
        })
        .sort(byDateDesc)
    markRenewed(registrations, g => `${g.type}|${g.state}`, g => dateValue(g.date))
    registrations.filter(g => g.renewed).forEach(g => Object.assign(g, { status: 'Renewed', tone: 'neutral' }))

    const einRelation = (tin) => {
        const value = digits(tin)
        if (value.length !== 9) return ''
        if (value === appEin) return 'Has the application\'s EIN'
        if (value === reportEin) return 'Same EIN as this company'
        return ein.others.some(o => digits(o.value) === value) ? 'EIN linked to this company' : ''
    }
    const connected = [
        ...list(r.ConnectedBusinessSection?.ConnectedBusinessRecords?.ConnectedBusinessRecord).map((c, index) => ({
            name: titleCase(c.CompanyName), kind: 'Business', address: formatAddress(c.Address), place: placeOf(c.Address), raw: c, index,
            ein: digits(c.TIN).length === 9 ? formatEin(digits(c.TIN)) : '', einRelation: einRelation(c.TIN),
            requested: Boolean(c.alreadyRequested), requestedDate: formatDate(c.lastRequestedDate),
        })),
        ...list(r.ParentSection?.Parents?.Parent).map(p => ({
            name: titleCase(p.CompanyName) || personName(p.Name), kind: 'Parent company', address: formatAddress(p.Address), place: placeOf(p.Address),
            phone: formatPhone(p.PhoneInfo?.Phone10), disconnected: p.Disconnected === true, hasRecords: p.HasDerog === true,
        })),
        ...list(r.AssociateSection?.BusinessAssociates?.BusinessAssociate).map(a => {
            const links = associateLinks(a.Role)
            return {
                name: titleCase(a.CompanyName), kind: 'Associate', address: formatAddress(a.Address), place: placeOf(a.Address),
                role: links.map(l => l.label).join(', '), flagged: links.some(l => l.flag),
                flagLabel: capitalize(links.filter(l => l.flag).map(l => l.label).join(', ')),
            }
        }),
    ].filter(c => c.name)

    const dnb = list(r.DunBradStreetSection?.Dnb)[0]
    const oldYear = now.getFullYear() - 2
    const estimates = []
    if (dnb) {
        const dnbYear = yearOf(dnb.AnnualSalesRevisionDate) || yearOf(dnb.ReportDate)
        if (dnb.AnnualSalesVolume && Number(dnb.AnnualSalesVolume) > 0)
            estimates.push({ label: 'D&B sales volume', value: formatMoney(dnb.AnnualSalesVolume), asOf: dnbYear, faded: dnbYear && dnbYear < oldYear, source: 'D&B' })
        if (dnb.EmployeesTotal && Number(dnb.EmployeesTotal) > 0)
            estimates.push({ label: 'D&B employees', value: String(Number(dnb.EmployeesTotal)), asOf: yearOf(dnb.ReportDate) || dnbYear, faded: dnbYear && dnbYear < oldYear, source: 'D&B' })
    }
    const finance = list(r.FinanceSection?.Finances?.Finance).filter(f => f.AnnualSales)
        .sort((a, b) => dateValue(b.SalesDate) - dateValue(a.SalesDate))[0]
    if (finance) {
        const year = yearOf(finance.SalesDate)
        const sourceCode = list(finance.SourceDocs?.SourceDoc)[0]?.Source
        const source = ['ER', 'Q3', 'E5'].includes(sourceCode) ? 'Experian' : sourceCode === 'DF' ? 'LexisNexis' : 'business directories'
        estimates.push({ label: 'Annual sales', value: formatMoney(finance.AnnualSales), asOf: year, faded: year && year < oldYear, source })
    }
    const experianProfile = list(experianReport?.Demographics5600?.Demographic)[0]
    if (experianProfile) {
        const employees = Number(experianProfile.EmployeeSizeActual) > 0 ? String(Number(experianProfile.EmployeeSizeActual)) : experianProfile.EmployeeSize || ''
        if (employees) estimates.push({ label: 'Experian employees', value: String(employees).replace(/\s+/g, ' '), source: 'Experian' })
        if (Number(experianProfile.SalesAcutal) > 0) estimates.push({ label: 'Experian sales', value: formatMoney(experianProfile.SalesAcutal), source: 'Experian' })
    }
    if (dnb && Number(dnb.EmployeesHere) > 0 && Number(dnb.EmployeesHere) !== Number(dnb.EmployeesTotal))
        estimates.push({ label: 'D&B employees at this location', value: String(Number(dnb.EmployeesHere)), source: 'D&B' })
    estimates.sort((a, b) => /employees/i.test(a.label) - /employees/i.test(b.label))
    const estimateSources = [...new Set(estimates.map(e => e.source))]

    const isYes = (value) => String(value ?? '').trim() !== '' && !/^(N|NO|FALSE|0)$/i.test(String(value).trim())
    const hotList = dnb?.HotList || {}
    const changeAlerts = [
        ['New business', hotList.NewIndicator, hotList.NewChangeDate],
        ['Ownership changed', hotList.OwnershipChangeIndicator, hotList.OwnershipChangeDate],
        ['CEO changed', hotList.CeoChangeIndicator, hotList.CeoChangeDate],
        ['Name changed', hotList.CompanyNameChangeInd, hotList.CompanyNameChgDate],
        ['Address changed', hotList.AddressChangeIndicator, hotList.AddressChangeDate],
        ['Phone changed', hotList.TelephoneChangeIndicator, hotList.TelephoneChangeDate],
    ].filter(([, flag, date]) => isYes(flag) || dateValue(date))
        .map(([label, , date]) => ({ label, date: formatMonthYear(date), recent: isWithinLastYear(date, now) }))

    const recentOwnerChange = changeAlerts.some(a => a.recent && /Ownership|CEO/.test(a.label))

    const signed = (sign, value) => (value != null && value !== '' && !Number.isNaN(Number(value)) ? `${sign === '-' ? '-' : ''}${Number(value)}` : '')
    const growth = [
        ['Employees, trend year', signed(dnb?.TrendEmploymentTotalSign, dnb?.TrendEmploymentTotal)],
        ['Employees, base year', signed(dnb?.BaseEmploymentTotalSign, dnb?.BaseEmploymentTotal)],
        ['Employee growth', signed(dnb?.PercentageEmploymentGrowthSign, dnb?.PercentageEmploymentGrowth) && `${signed(dnb.PercentageEmploymentGrowthSign, dnb.PercentageEmploymentGrowth)}%`],
        ['Sales, trend year', signed(dnb?.TrendSalesSign, dnb?.TrendSales) && formatMoney(signed(dnb.TrendSalesSign, dnb.TrendSales))],
        ['Sales, base year', signed(dnb?.BaseSalesSign, dnb?.BaseSales) && formatMoney(signed(dnb.BaseSalesSign, dnb.BaseSales))],
        ['Sales growth', signed(dnb?.PercentageSalesGrowthSign, dnb?.PercentageSalesGrowth) && `${signed(dnb.PercentageSalesGrowthSign, dnb.PercentageSalesGrowth)}%`],
    ].filter(([, value]) => value).map(([label, value]) => ({ label, value }))

    const experianHeader = experianReport?.BusinessHeader || {}

    const yesNo = (value) => (isYes(value) ? 'Yes' : /^(N|NO|FALSE)$/i.test(String(value ?? '').trim()) ? 'No' : '')
    const sicList = (sics, code, description) => [...new Set(list(sics).map(s => [s?.[code], titleCase(s?.[description])].filter(Boolean).join(' ')))].join('; ')
    const profileDetails = [
        ['Line of business', titleCase(dnb?.LineOfBusinessDescription)],
        ['Year started (D&B)', dnb?.YearStarted && dnb.YearStarted !== '0' ? String(dnb.YearStarted) : ''],
        ['Years in business (Experian)', Number(experianProfile?.YearsInBusinessActual) > 0 ? String(experianProfile.YearsInBusinessActual) : ''],
        ['Known to D&B', [formatDate(dnb?.DateFirstSeen), formatDate(dnb?.DateLastSeen)].filter(Boolean).join(' to ')],
        ['Structure', titleCase(dnb?.StructureTypeDecoded)],
        ['Business type (Experian)', titleCase(experianProfile?.BusinessType)],
        ['Establishment', titleCase(dnb?.TypeOfEstablishmentDecoded)],
        ['Owns or rents', titleCase(dnb?.OwnsRentsDecoded)],
        ['Square footage', Number(dnb?.SquareFootage) > 0 ? Number(dnb.SquareFootage).toLocaleString('en-US') : ''],
        ['Net worth', signed(dnb?.NetWorthSign, dnb?.NetWorth) && formatMoney(signed(dnb.NetWorthSign, dnb.NetWorth))],
        ['Small business', yesNo(dnb?.SmallBusinessIndicator)],
        ['Minority owned', yesNo(dnb?.MinorityOwned)],
        ['Foreign owned', yesNo(dnb?.ForeignOwned)],
        ['Public company', yesNo(dnb?.PublicIndicator)],
        ['Importer / exporter', yesNo(dnb?.ImporterExporterIndicator)],
        ['Manufacturing here', yesNo(dnb?.ManufacturingHereIndicator)],
        ['Parent company', titleCase(dnb?.ParentCompanyName)],
        ['Ultimate parent', titleCase(dnb?.UltimateCompanyName)],
        ['Trade style', titleCase(dnb?.TradeStyle)],
        ['Incorporated', [formatDate(dnb?.DateOfIncorporation), STATES[dnb?.StateOfIncorporation] || dnb?.StateOfIncorporation].filter(Boolean).join(', ')],
        ['SIC (D&B)', sicList(dnb?.SICs?.SIC, 'SICCode', 'SICCodeDescription')],
        ['SIC (Experian)', sicList(experianProfile?.SICs?.SIC, 'Code', 'Description')],
        ['Metro area', dnb?.MsaName || ''],
        ['Phone (D&B)', formatPhone(dnb?.Phone10)],
        ['Address (D&B)', formatAddress(dnb?.Address)],
        ['Mailing address (D&B)', formatAddress(dnb?.MailingAddress)],
        ['Accounting firm', titleCase(dnb?.AccountingFirmName)],
        ['Experian name', [titleCase(experianHeader.CompanyName), experianHeader.DBAName && `DBA ${titleCase(experianHeader.DBAName)}`].filter(Boolean).join(', ')],
        ['Experian description', capitalize(String(experianHeader.BusinessDescription || '').toLowerCase().trim())],
        ['In Experian since', formatDate(experianHeader.FileEstablishDate)],
        ['Known to Experian', [formatDate(experianHeader.DateFirstSeen), formatDate(experianHeader.DateLastSeen)].filter(Boolean).join(' to ')],
        ['Phone (Experian)', formatPhone(experianHeader.PhoneNumber)],
        ['Address (Experian)', formatAddress(experianHeader.Address)],
        ['SIC (Experian file)', experianHeader.SICCode || ''],
        ['Disputed with Experian', yesNo(experianHeader.DisputeIndicator) === 'Yes' ? 'Yes, the company disputes part of its Experian file' : ''],
        ['Past names and addresses (Experian)', list(experianReport?.HistoricalCompanyInfo?.CompanyInfo)
            .map(h => [titleCase(h?.CompanyName), formatAddress(h?.Address), [formatMonthYear(h?.DateFirstSeen), formatMonthYear(h?.DateLastSeen)].filter(Boolean).join('–')].filter(Boolean).join(' · '))
            .filter(Boolean).join('; ')],
        ['Other names', aliases.map(a => [a.name, a.seen && `(${a.seen})`].filter(Boolean).join(' ')).join('; ')],
        ['Banks (Experian)', list(experianReport?.BankingDetails?.BankingDetail).map(b => [titleCase(b?.Name), titleCase(b?.Relationship),
            [formatMonthYear(b?.DateOpened) && `opened ${formatMonthYear(b.DateOpened)}`, formatMonthYear(b?.DateClosed) && `closed ${formatMonthYear(b.DateClosed)}`].filter(Boolean).join(', '),
            Number(b?.Balance) > 0 && `balance ${formatMoney(b.Balance)}`, formatAddress(b?.Address) || titleCase(b?.StateName), formatPhone(b?.PhoneNumber),
            isYes(b?.DisputeIndicator) && 'disputed by the company'].filter(Boolean).join(' · ')).filter(Boolean).join('; ')],
        ['Experian notes', list(experianReport?.SNPs?.SNP).map(n => capitalize(String(n?.DataPrintLine || '').toLowerCase().trim())).filter(Boolean).join('; ')],
        ['DUNS number', dnb?.DunsNumber || ''],
        ['Parent DUNS', dnb?.ParentDunsNumber || ''],
        ['Headquarters DUNS', dnb?.HeadquartersDunsNumber || ''],
        ['Ultimate DUNS', dnb?.UltimateDunsNumber || ''],
    ].filter(([, value]) => value).map(([label, value]) => ({ label, value }))
    ;[[r.DunBradStreetSection, 'DunBradStreetSection', 'D&B'], [experianReport, 'ExperianBusinessReportSection/ExperianBusinessReports/ExperianBusinessReport[]', 'Experian'],
        [list(r.ParentSection?.Parents?.Parent), 'ParentSection/Parents/Parent[]', 'Parent company']]
        .forEach(([records, root, source]) => (moreFromLexis(records, root)?.details || [])
            .forEach(d => profileDetails.push({ label: `${source}: ${d.label}`, value: d.value })))

    const mainAddress = formatAddress(best.Address)
    const locations = list(r.OperationsSitesSection?.OperationsSites?.OperationSite).flatMap(site =>
        list(site?.Addresses?.Address).map(a => {
            const phones = list(a?.Phones?.Phone).filter(p => p?.Phone10)
            const disconnected = phones.filter(p => p.Disconnected === true).length
            const address = formatAddress(a?.Address)
            return {
                state: STATES[a?.Address?.State] || titleCase(site?.StateName) || a?.Address?.State || '',
                main: Boolean(address && address === mainAddress),
                title: address || 'Address not given',
                facts: [titleCase(a?.Address?.County) && `${titleCase(a.Address.County)} County`, a?.MsaName,
                    phones.map(p => formatPhone(p.Phone10)).filter(Boolean).join(', ')].filter(Boolean).join(' · '),
                chips: [
                    address && address === mainAddress && { label: 'Main address', tone: 'neutral' },
                    disconnected > 0 && { label: disconnected === phones.length ? 'Phone disconnected' : `${disconnected} of ${phones.length} phones disconnected`, tone: 'watch' },
                ].filter(Boolean),
                details: [
                    ['Metro area', a?.MsaName],
                    ...phones.map((p, n) => [phones.length > 1 ? `Phone ${n + 1}` : 'Phone', [formatPhone(p.Phone10), titleCase(p.LineType), titleCase(p.ListingName),
                        titleCase(p.ListingType), p.ActiveEDA === true && 'listed in directory assistance', p.Disconnected === true && 'disconnected',
                        [...new Set([formatMonthYear(p.FromDate), formatMonthYear(p.ToDate)].filter(Boolean))].join('–')].filter(Boolean).join(' · ')]),
                    ['Property records', a?.PropertyLink ? 'Lexis has property records for this address' : ''],
                ].filter(([, value]) => value).map(([label, value]) => ({ label, value })),
            }
        }))
        .sort((a, b) => b.main - a.main || a.state.localeCompare(b.state))
    const locationStates = [...new Set(locations.map(l => l.state).filter(Boolean))]

    const vehicles = r.MotorVehicleSection?.MotorVehicleRecords || {}
    const property = r.PropertySection?.PropertyRecords || {}
    const vehiclesCurrent = Number(vehicles.TotalCurrentRecordCount ?? vehicles.CurrentRecordCount ?? 0) || 0
    const vehiclesPrior = Number(vehicles.TotalPriorRecordCount ?? vehicles.PriorRecordCount ?? 0) || 0
    const propertyCurrent = Number(property.TotalCurrentRecordsCount ?? property.CurrentRecordsCount ?? r.PropertySection?.TotalPropertyRecordCount ?? 0) || 0
    const propertyPrior = Number(property.TotalPriorRecordsCount ?? property.PriorRecordsCount ?? 0) || 0
    const watercraft = Number(r.WatercraftSection?.TotalWatercraftRecordCount || 0)
    const aircraft = Number(r.AircraftSection?.TotalAircraftRecordCount || 0)
    const kv = (pairs) => pairs.filter(([, value]) => value !== '' && value != null && value !== false).map(([label, value]) => ({ label, value: String(value) }))
    const isYesValue = (value) => value === true || /^(Y|YES|TRUE|1)$/i.test(String(value ?? '').trim())
    const partyLine = (p) => [titleCase(p?.CompanyName) || personName(p?.Name), formatAddress(p?.Address)].filter(Boolean).join(' · ')

    const vehicleItem = (v, current) => {
        const parties = list(v?.Parties?.Party)
        const types = parties.map(p => String(p?.PartyTypeDescription || '').toUpperCase())
        const expiries = parties.map(p => p?.RegistrationExpirationDate).filter(d => dateValue(d))
        const expired = current && expiries.length > 0 && expiries.every(d => dateValue(d) < now.getTime())
        const engine = [v?.EngineSize && `${v.EngineSize}L`, Number(v?.NumberOfCylinders) > 0 && `${v.NumberOfCylinders} cylinders`].filter(Boolean).join(', ')
        const drive = isYesValue(v?.FourWheelDrive) ? 'Four-wheel drive' : isYesValue(v?.FrontWheelDrive) ? 'Front-wheel drive' : ''
        return {
            group: 'vehicles', current, sortKey: Number(v?.ModelYear || 0),
            title: [v?.ModelYear, titleCase([v?.Make, v?.Series].filter(Boolean).join(' '))].filter(Boolean).join(' ') || 'Vehicle',
            facts: [titleCase(v?.Style || v?.VehicleType), v?.StateOfOrigin].filter(Boolean).join(' · '),
            chips: [
                types.some(t => /LESS/.test(t)) && { label: 'Leased', tone: 'watch' },
                types.some(t => /LIEN/.test(t)) && { label: 'Lien', tone: 'watch' },
                expired && { label: 'Registration expired', tone: 'watch' },
            ].filter(Boolean),
            details: [
                ...parties.flatMap(p => kv([
                    [titleCase(p?.PartyTypeDescription) || 'Registered to', partyLine(p)],
                    ['License plate', [p?.LicensePlateState, p?.LicensePlate, p?.LicensePlateType && `(${titleCase(p.LicensePlateType)})`].filter(Boolean).join(' ')],
                    ['Previous plate', p?.PreviousLicensePlate],
                    ['Registered', [formatDate(p?.RegistrationDate), p?.RegistrationExpirationDate && `expires ${formatDate(p.RegistrationExpirationDate)}`].filter(Boolean).join(', ')],
                    ['First registered', formatDate(p?.OriginalRegistrationDate)],
                    ['Title', [formatDate(p?.TitleDate), p?.TitleNumber && `No. ${p.TitleNumber}`, titleCase(p?.TitleStatus)].filter(Boolean).join(' · ')],
                    ['Odometer', Number(p?.OdometerMileage) > 0 && `${Number(p.OdometerMileage).toLocaleString('en-US')} miles`],
                ])),
                ...kv([
                    ['VIN', v?.VIN],
                    ['Color', titleCase(v?.Color)],
                    ['Engine', engine],
                    ['Fuel', titleCase(v?.FuelTypeName)],
                    ['Drive', drive],
                    ['Base price', Number(String(v?.BasePrice || '').replace(/[^0-9.]/g, '')) > 0 && formatMoney(v.BasePrice)],
                    ['Use', titleCase(v?.VehicleUse)],
                ]),
            ],
        }
    }

    const propertyItem = (p) => {
        const a = p?.Accessor || {}
        const parties = list(p?.Parties?.Party)
        const owners = parties.flatMap(x => list(x?.Owners?.Owner))
        const sellers = parties.flatMap(x => list(x?.Sellers?.Seller))
        const borrowers = parties.flatMap(x => list(x?.Borrowers?.Borrower))
        const mortgages = parties.flatMap(x => list(x?.Mortgages?.Mortgage))
        const foreclosures = parties.flatMap(x => list(x?.Foreclosures?.Foreclosure))
        const defaults = parties.flatMap(x => list(x?.NoticeOfDefaults?.NoticeOfDefault))
        const deed = p?.Deeds || {}
        const value = p?.TotalMarketValue || a.MarketTotalValue
        const foreclosed = isYesValue(p?.IsForeclosed) || foreclosures.length > 0
        const noticeOfDefault = isYesValue(p?.IsNoticeOfDefault) || defaults.length > 0
        return {
            group: 'property', current: isYesValue(p?.CurrentRecord), sortKey: dateValue(p?.RecordingDate || p?.SaleDate || p?.AssessmentDate),
            title: formatAddress(p?.PropertyAddress) || 'Property',
            facts: [titleCase(a.StandardizedLandUseDesc || p?.AddressType), value && `Market value ${formatMoney(value)}`,
                owners[0] && `Owner ${titleCase(owners[0].CompanyName) || personName(owners[0].Name)}`].filter(Boolean).join(' · '),
            chips: [
                foreclosed && { label: 'Foreclosure', tone: 'declined' },
                noticeOfDefault && { label: 'Notice of default', tone: 'declined' },
                (mortgages.length > 0 || deed.LenderName) && { label: 'Mortgage', tone: 'watch' },
            ].filter(Boolean),
            foreclosed, noticeOfDefault,
            details: kv([
                ['Owners', owners.map(partyLine).filter(Boolean).join('; ')],
                ['Owner relationship', titleCase(a.AssesseeRelationshipDesc)],
                ['Market value', [value && formatMoney(value), a.MarketValueYear && `(${a.MarketValueYear})`].filter(Boolean).join(' ')],
                ['Land value', p?.MarketLandValue && formatMoney(p.MarketLandValue)],
                ['Assessed value', [(p?.AssessedValue || a.AssessedTotalValue) && formatMoney(p?.AssessedValue || a.AssessedTotalValue), (a.AssessedValueYear || yearOf(p?.AssessmentDate)) && `(${a.AssessedValueYear || yearOf(p.AssessmentDate)})`].filter(Boolean).join(' ')],
                ['Tax', [a.TaxAmount && formatMoney(a.TaxAmount), a.TaxYear && `(${a.TaxYear})`].filter(Boolean).join(' ')],
                ['Purchase', [(p?.PurchasePrice || p?.SalesPrice) && formatMoney(p?.PurchasePrice || p?.SalesPrice), formatDate(p?.SaleDate || p?.ContractDate)].filter(Boolean).join(' on ')],
                ['Sellers', sellers.map(partyLine).filter(Boolean).join('; ')],
                ['Mortgages', [...mortgages.map(m => [titleCase(m?.LenderName), m?.LoanAmount && formatMoney(m.LoanAmount), titleCase(m?.LoanType || m?.Description), formatDate(m?.LoanDate || m?.RecordingDate)].filter(Boolean).join(' · ')),
                    deed.LenderName && [titleCase(deed.LenderName), deed.FirstTdLoanAmount && formatMoney(deed.FirstTdLoanAmount), titleCase(deed.FirstTdLoanTypeDesc)].filter(Boolean).join(' · ')].filter(Boolean).join('; ')],
                ['Borrowers', borrowers.map(partyLine).filter(Boolean).join('; ')],
                ['Foreclosure', foreclosures.map(f => [titleCase(f?.DocumentType), formatDate(f?.RecordingDate), f?.PlaintiffName1 && `by ${titleCase(f.PlaintiffName1)}`].filter(Boolean).join(' · ')).join('; ')],
                ['Notice of default', defaults.map(n => [formatDate(n?.RecordingDate), titleCase(n?.LenderCompanyName), n?.AuctionDate && `auction ${formatDate(n.AuctionDate)}`].filter(Boolean).join(' · ')).join('; ')],
                ['Land use', titleCase(a.CountyLandUseDescription)],
                ['Building', [a.YearBuilt && `built ${a.YearBuilt}`, a.NumberOfStories && `${a.NumberOfStories} ${Number(a.NumberOfStories) === 1 ? 'story' : 'stories'}`, Number(a.LandSquareFootage) > 0 && `${Number(a.LandSquareFootage).toLocaleString('en-US')} sq ft of land`,
                    a.NumberOfBedrooms && `${a.NumberOfBedrooms} bedroom${Number(a.NumberOfBedrooms) === 1 ? '' : 's'}`, a.NumberOfBaths && `${a.NumberOfBaths} bath${Number(a.NumberOfBaths) === 1 ? '' : 's'}`, titleCase(a.HeatingDesc), titleCase(a.AirConditioningDesc), titleCase(a.ExteriorWallsDesc), titleCase(a.RoofTypeDesc)].filter(Boolean).join(', ')],
                ['Record', [titleCase(a.RecordTypeDesc || p?.DocumentType), formatDate(p?.RecordingDate)].filter(Boolean).join(', ')],
                ['Deed', [titleCase(deed.DocumentTypeDesc), deed.DocumentNumber && `No. ${deed.DocumentNumber}`, titleCase(deed.TitleCompanyName), deed.TotalTransferTax && `transfer tax ${formatMoney(deed.TotalTransferTax)}`,
                    deed.RecorderBookNumber && `book ${deed.RecorderBookNumber} page ${deed.RecorderPageNumber || '-'}`].filter(Boolean).join(' · ')],
                ['Parcel number (APN)', p?.APN],
                ['Legal description', [a.LegalSubdivisionName && titleCase(a.LegalSubdivisionName), a.LegalLotNumber && `lot ${a.LegalLotNumber}`].filter(Boolean).join(', ')],
            ]),
        }
    }

    const craftParties = (c) => [...list(c?.CurrentParties?.CurrentParty), ...list(c?.PriorParties?.PriorParty)]
    const watercraftItem = (w, current) => ({
        group: 'boats', current, sortKey: Number(w?.ModelYear || 0),
        title: [w?.ModelYear, titleCase(w?.VesselMake), w?.VesselName && `"${titleCase(w.VesselName)}"`].filter(Boolean).join(' ') || 'Boat',
        facts: [w?.Length && `${w.Length} ft`, titleCase(w?.Propulsion), titleCase(w?.Use), w?.StateJurisdiction].filter(Boolean).join(' · '),
        chips: [],
        details: kv([
            ['Registered to', craftParties(w).map(partyLine).filter(Boolean).join('; ')],
            ['Registration', [w?.RegistrationNumber, titleCase(w?.RegistrationStatus), formatDate(w?.RegistrationDate), w?.ExpirationDate && `expires ${formatDate(w.ExpirationDate)}`].filter(Boolean).join(' · ')],
            ['Hull number', w?.HullNumber],
        ]),
    })
    const aircraftItem = (c, current) => ({
        group: 'aircraft', current, sortKey: Number(c?.ModelYear || 0),
        title: [c?.ModelYear, titleCase([c?.Manufacturer, c?.Model].filter(Boolean).join(' '))].filter(Boolean).join(' ') || 'Aircraft',
        facts: [titleCase(c?.AircraftType), c?.Engines && `${c.Engines} engine${Number(c.Engines) === 1 ? '' : 's'}`, c?.RegistrationNumber && `N-number ${c.RegistrationNumber}`].filter(Boolean).join(' · '),
        chips: [],
        details: kv([
            ['Registered to', craftParties(c).map(partyLine).filter(Boolean).join('; ')],
            ['Registration', [formatDate(c?.RegistrationDate), formatAddress(c?.RegistrationAddress)].filter(Boolean).join(' · ')],
            ['Serial number', c?.SerialNumber],
            ['Aircraft number', c?.AircraftNumber],
        ]),
    })

    const boats = r.WatercraftSection?.WatercraftRecords || {}
    const planes = r.AircraftSection?.AircraftRecords || {}
    const withMore = (item, record, root) => ({ ...item, more: moreFromLexis(record, root) })
    const byNewest = (a, b) => b.sortKey - a.sortKey
    const assetGroups = [
        { id: 'vehicles', label: 'Vehicles', priorLabel: 'prior vehicle',
            current: list(vehicles.CurrentVehicles?.CurrentVehicle).map(v => withMore(vehicleItem(v, true), v, 'MotorVehicleSection/MotorVehicleRecords/CurrentVehicles/CurrentVehicle[]')).sort(byNewest),
            prior: list(vehicles.PriorVehicles?.PriorVehicle).map(v => withMore(vehicleItem(v, false), v, 'MotorVehicleSection/MotorVehicleRecords/PriorVehicles/PriorVehicle[]')).sort(byNewest) },
        { id: 'property', label: 'Property', priorLabel: 'prior property record',
            ...(() => {
                const items = list(property.Properties?.Property).map(p => withMore(propertyItem(p), p, 'PropertySection/PropertyRecords/Properties/Property[]')).sort(byNewest)
                return items.some(i => i.current) ? { current: items.filter(i => i.current), prior: items.filter(i => !i.current) } : { current: items, prior: [] }
            })() },
        { id: 'boats', label: 'Boats', priorLabel: 'prior boat',
            current: list(boats.CurrentWatercrafts?.Watercraft).map(w => withMore(watercraftItem(w, true), w, 'WatercraftSection/WatercraftRecords/CurrentWatercrafts/Watercraft[]')).sort(byNewest),
            prior: list(boats.PriorWatercrafts?.Watercraft).map(w => withMore(watercraftItem(w, false), w, 'WatercraftSection/WatercraftRecords/PriorWatercrafts/Watercraft[]')).sort(byNewest) },
        { id: 'aircraft', label: 'Aircraft', priorLabel: 'prior aircraft',
            current: list(planes.CurrentAircrafts?.Aircraft).map(c => withMore(aircraftItem(c, true), c, 'AircraftSection/AircraftRecords/CurrentAircrafts/Aircraft[]')).sort(byNewest),
            prior: list(planes.PriorAircrafts?.Aircraft).map(c => withMore(aircraftItem(c, false), c, 'AircraftSection/AircraftRecords/PriorAircrafts/Aircraft[]')).sort(byNewest) },
    ].filter(g => g.current.length + g.prior.length > 0)
    const propertyDistress = assetGroups.flatMap(g => [...g.current, ...g.prior]).filter(i => i.foreclosed || i.noticeOfDefault).length
    const assets = [
        (vehiclesCurrent || vehiclesPrior) && { label: 'Vehicles', value: `${vehiclesCurrent} current`, sub: vehiclesPrior ? `${vehiclesPrior} prior` : '' },
        (propertyCurrent || propertyPrior) && { label: 'Property', value: `${propertyCurrent} current`, sub: propertyPrior ? `${propertyPrior} prior` : '' },
        watercraft && { label: 'Boats', value: String(watercraft) },
        aircraft && { label: 'Aircraft', value: String(aircraft) },
    ].filter(Boolean)

    const bankruptcyFiler = (b) => {
        const original = String(b.OriginalFilingType || '').toUpperCase()
        const filer = String(b.FilerType || '').toUpperCase()
        if (original === 'BUSINESS' || filer === 'BUSINESS') return 'Business'
        if (filer === 'JOINT') return 'Individual (joint)'
        if (original === 'INDIVIDUAL' || filer === 'INDIVIDUAL') return 'Individual'
        return ''
    }
    const bankruptcyRows = list(r.BankruptcySection?.AsDebtors?.AsDebtor).map(b => ({
        more: moreFromLexis(b, 'BankruptcySection/AsDebtors/AsDebtor[]'),
        type: titleCase(/^CHAPTER/i.test(b.FilingType || '') ? b.FilingType : b.FilingType || b.OriginalFilingType),
        filer: bankruptcyFiler(b),
        voluntary: titleCase(b.FilingStatus),
        date: b.OriginalFilingDate,
        dateLabel: formatDate(b.OriginalFilingDate),
        status: titleCase(b.Status || b.FilingStatus),
        statusTone: /DISMISS|DISCHARG|CLOSED|TERMINAT/i.test(b.Status || b.FilingStatus || '') ? 'neutral' : 'declined',
        statusDate: formatDate(b.StatusDate),
        court: titleCase(b.CourtLocation),
        caseNumber: b.OriginalCaseNumber || '',
        details: [
            ['Filed as', titleCase(b.FilingStatus)],
            ['Original filing type', titleCase(b.OriginalFilingType)],
            ['Status date', formatDate(b.StatusDate)],
            ['Court', [titleCase(b.CourtLocation), b.CourtCode && `code ${b.CourtCode}`].filter(Boolean).join(', ')],
            ['Case number', b.OriginalCaseNumber],
            ['Comment', capitalize(String(b.Comment || '').toLowerCase().trim())],
            ['Debtors', list(b.Debtors?.Debtor).map(x => [partyName(x), formatAddress(x?.Address), maskedSsn(x?.SSN) && `SSN ${maskedSsn(x.SSN)}`].filter(Boolean).join(' · ')).filter(Boolean).join('; ')],
            ['Attorneys', list(b.Attorneys?.Attorney).map(x => [partyName(x), formatAddress(x?.Address)].filter(Boolean).join(' · ')).filter(Boolean).join('; ')],
            ['Trustees', list(b.Trustees?.Trustee).map(x => [partyName(x), formatAddress(x?.Address)].filter(Boolean).join(' · ')).filter(Boolean).join('; ')],
            ['Raw status', b.StatusRaw && b.StatusRaw !== b.Status ? b.StatusRaw : ''],
        ].filter(([, value]) => value).map(([label, value]) => ({ label, value })),
    })).sort(byDateDesc)
    const bankruptcies = Math.max(Number(r.BankruptcySection?.DerogSummaryCntBankruptcy || 0), bankruptcyRows.length)
    const liens = list(r.LienSection?.JudgmentsLiens?.JudgmentLien).map(l => {
        const types = list(l?.Filings?.Filing).map(f => f?.FilingType).concat(l?.FilingType || [])
        const status = lienStatus(types, l?.ReleaseDate)
        return {
            more: moreFromLexis(l, 'LienSection/JudgmentsLiens/JudgmentLien[]'),
            type: cleanLienType(l?.OrigFilingType || types[0]) || 'Lien or judgment',
            creditor: list(l?.Creditors?.Creditor).map(partyName).filter(Boolean)[0] || '',
            eviction: String(l?.Eviction || '').toUpperCase().startsWith('Y') || types.some(t => /FORCIBLE ENTRY/i.test(String(t))),
            date: l?.OrigFilingDate,
            dateLabel: formatDate(l?.OrigFilingDate),
            amount: l?.Amount && Number(String(l.Amount).replace(/[^0-9.]/g, '')) > 0 ? formatMoney(l.Amount) : '',
            amountValue: numberOr(String(l?.Amount ?? '').replace(/[^0-9.]/g, '')),
            number: l?.OrigFilingNumber || '',
            state: l?.FilingJurisdiction || '',
            status,
            releaseDate: formatDate(l?.ReleaseDate),
            details: [
                ['Filing number', l?.OrigFilingNumber],
                ['Filing office', [...new Set(list(l?.Filings?.Filing).map(f => [titleCase(f?.Agency), titleCase(f?.AgencyCounty) && `${titleCase(f.AgencyCounty)} County`, f?.AgencyState].filter(Boolean).join(', ')).filter(Boolean))].join('; ')],
                ['Filings', list(l?.Filings?.Filing).map(f => [titleCase(f?.FilingType), formatDate(f?.FilingDate), f?.FilingNumber && `No. ${f.FilingNumber}`,
                    titleCase(f?.FilingStatus), f?.ReleaseDate && `released ${formatDate(f.ReleaseDate)}`].filter(Boolean).join(' · ')).filter(Boolean).join('; ')],
                ['Released', formatDate(l?.ReleaseDate)],
                ['Owed to', list(l?.Creditors?.Creditor).map(partyName).filter(Boolean).join('; ')],
                ['Debtors', list(l?.Debtors?.Debtor).map(x => [partyName(x), formatAddress(x?.Address), maskedSsn(x?.SSN) && `SSN ${maskedSsn(x.SSN)}`].filter(Boolean).join(' · ')).filter(Boolean).join('; ')],
                ['Eviction', String(l?.Eviction || '') ? titleCase(l.Eviction) : ''],
            ].filter(([, value]) => value).map(([label, value]) => ({ label, value })),
        }
    }).sort(byDateDesc)
    const openLiens = liens.filter(l => l.status === 'Open').length

    const payments = paymentHistory(experianReport, now)
    const experianDerogatory = experianRecords(experianReport)
    experianDerogatory.forEach(x => {
        x.inLexis = x.kind !== 'Bankruptcy' && liens.some(l => (x.number && x.number === l.number)
            || (x.amountValue > 0 && x.amountValue === l.amountValue && dateValue(x.date) === dateValue(l.date)))
    })
    const experianExtra = experianDerogatory.filter(x => !x.inLexis)
    const latePayer = Boolean(payments && (payments.tone === 'declined' || payments.worsening))

    const issuerLabel = (issuer) => {
        const match = String(issuer || '').match(/^([A-Z]{2}) (.*)$/)
        return match && STATES[match[1]] ? `${match[1]} ${titleCase(match[2])}` : titleCase(issuer)
    }
    const MEDICAL_LICENSE = /MEDIC|NURSING|\bDEA\b|PHARMAC|DENTAL|OSTEOPATH|CHIROPRACT|PODIATR|OPTOMETR|PHYSICIAN/i
    const licenses = list(r.LicenseSection?.LicenseRecords?.Licenses?.License).map(l => {
        const expiry = dateValue(l.ExpirationDate)
        return {
            number: l.LicenseNumber || '', description: titleCase(l.Description), issuer: issuerLabel(l.Issuer),
            issued: formatDate(l.IssueDate), expires: formatDate(l.ExpirationDate),
            expired: Boolean(expiry && expiry < now.getTime()),
            expiresSoon: Boolean(expiry && expiry >= now.getTime() && expiry - now.getTime() < 90 * 86400000),
            expiryValue: expiry,
            pending: /PENDING|APPLICATION/i.test(String(l.Description || '')),
            medical: MEDICAL_LICENSE.test(`${l.Issuer || ''} ${l.Description || ''}`),
            sort: dateValue(l.IssueDate),
        }
    }).sort((a, b) => b.medical - a.medical || b.sort - a.sort)
    markRenewed(licenses.filter(l => !l.pending), l => `${l.description}|${l.issuer}|${l.number}`, l => l.sort)
    const filingDate = (value) => {
        if (value && typeof value === 'object') return formatDate(value)
        const text = String(value || '').trim()
        const compact = text.match(/^(\d{4})(\d{2})(\d{2})$/)
        return compact ? `${compact[2]}/${compact[3]}/${compact[1]}` : formatDate(text)
    }
    const planParty = (x) => {
        if (!x) return null
        const name = titleCase(x.FullName) || (typeof x.Name === 'object' ? personName(x.Name) : titleCase(x.Name))
        return name || x.Address ? { name, address: formatAddress(x.Address), phone: formatPhone(x.Phone), ein: digits(x.FEIN).length === 9 ? formatEin(digits(x.FEIN)) : '' } : null
    }
    const countOrBlank = (value) => (numberOr(value) != null ? Number(value) : null)
    const pensionPlans = list(r.IRS5500Section?.IRS5500s?.IRS5500).map(p => {
        const sponsor = planParty(p.Sponsor) || (p.SponsorName ? { name: titleCase(p.SponsorName), address: '', phone: '', ein: '' } : null)
        const sponsorEin = digits(p.Sponsor?.FEIN || p.FEIN)
        const yes = (value) => /^(Y|YES|TRUE|1)$/i.test(String(value ?? '').trim())
        return {
            more: moreFromLexis(p, 'IRS5500Section/IRS5500s/IRS5500[]'),
            name: titleCase(p.PlanName).replace(/\b(401|403|457)\(([A-Z])\)/g, (m, n, letter) => `${n}(${letter.toLowerCase()})`) || 'Benefit plan',
            year: yearOf(p.FormPlanYearBeginDate) || '',
            sort: dateValue(p.FormPlanYearBeginDate),
            participants: countOrBlank(p.TotActivePartcpCnt),
            totalParticipants: countOrBlank(p.TotActRtdSepBenefCnt) ?? countOrBlank(p.TotPartcpBoyCnt),
            einDiffers: Boolean(sponsorEin.length === 9 && reportEin && sponsorEin !== reportEin),
            extension: yes(p.ExtApplicationFiledIndicator),
            union: yes(p.CollectiveBargainIndicator),
            details: [
                ['Sponsor', sponsor && [sponsor.name, sponsor.address, sponsor.phone].filter(Boolean).join(' · ')],
                ['Sponsor EIN', sponsorEin.length === 9 ? formatEin(sponsorEin) : ''],
                ['Administrator', (() => { const a = planParty(p.Administrator); return a && [a.name, a.address, a.phone, a.ein && `EIN ${a.ein}`].filter(Boolean).join(' · ') })()],
                ['Plan number', p.PlanNumber],
                ['Plan type', planTypes(p)],
                ['Funding', FUNDING[String(p.FundingArrangementCode || '').trim()] || ''],
                ['Plan in effect since', filingDate(p.PlanEffectiveDate)],
                ['Plan year', [filingDate(p.FormPlanYearBeginDate), p.FormTaxPRD && `to ${filingDate(p.FormTaxPRD)}`].filter(Boolean).join(' ')],
                ['Filed', filingDate(p.FormDate)],
                ['Participants', [
                    countOrBlank(p.TotPartcpBoyCnt) != null && `${p.TotPartcpBoyCnt} at start of year`,
                    countOrBlank(p.TotActivePartcpCnt) != null && `${p.TotActivePartcpCnt} active`,
                    countOrBlank(p.RtdSepPartcpRcvgCnt) != null && `${p.RtdSepPartcpRcvgCnt} retired or separated receiving benefits`,
                    countOrBlank(p.RtdSepPartcpFutCnt) != null && `${p.RtdSepPartcpFutCnt} entitled to future benefits`,
                    countOrBlank(p.BenefRcvgBnftCnt) != null && `${p.BenefRcvgBnftCnt} beneficiaries`,
                    countOrBlank(p.TotActRtdSepBenefCnt) != null && `${p.TotActRtdSepBenefCnt} total at year end`,
                    countOrBlank(p.PartcpAccountBalCnt) != null && `${p.PartcpAccountBalCnt} with account balances`,
                    countOrBlank(p.SepPartcpParttlVstCnt) != null && `${p.SepPartcpParttlVstCnt} separated, partly vested`,
                ].filter(Boolean).join(' · ')],
                ['Schedules filed', SCHEDULES.filter(([key]) => yes(p.ScheduleAttachments?.[`Sch${key}AttachedIndicator`])).map(([, label]) => label).join(', ')],
                ['Plan entity', PLAN_ENTITY[String(p.TypePlanEntityIndicator || '').trim()] || ''],
                ['Extension filed', yes(p.ExtApplicationFiledIndicator) ? 'Yes' : ''],
                ['Union (collective bargaining)', yes(p.CollectiveBargainIndicator) ? 'Yes' : ''],
                ['Signed by sponsor', [titleCase(p.SponsorSignedName), filingDate(p.SponsorSignedDate)].filter(Boolean).join(', ')],
                ['Signed by administrator', [titleCase(p.AdminSignedName), filingDate(p.AdminSignedDate)].filter(Boolean).join(', ')],
            ].filter(([, value]) => value).map(([label, value]) => ({ label, value: String(value) })),
        }
    }).sort((a, b) => b.sort - a.sort)
    const filedHeadcount = pensionPlans.find(p => p.participants != null)
    if (filedHeadcount) estimates.push({ label: 'Employees in benefit plan', value: String(filedHeadcount.participants), asOf: filedHeadcount.year, source: 'IRS Form 5500', filed: true })

    const derogatoryCount = bankruptcies + liens.length
    const experianOpenRecords = experianExtra.filter(x => x.open).length
    const sections = [
        { id: 'ucc', title: 'UCC filings', main: true, count: activeUccs.length, hasData: activeUccs.length + endedUccs.length + experianFilings.length > 0 || Boolean(experianCollateral),
            tone: recentUccs ? 'watch' : null },
        { id: 'people', title: 'People', main: true, count: people.length, hasData: people.length + linkedPeople.length + officers.length > 0, tone: flaggedPeople || deceasedCurrent ? 'declined' : null },
        { id: 'filings', title: 'Corporate filings', main: true, count: filings.length, hasData: filings.length + agents.length > 0,
            tone: badFilings.length || filingsWithOpenWarnings ? 'declined' : null },
        { id: 'licenses', title: 'Registrations and licenses', linkTitle: 'Registrations', main: true, count: registrations.length, hasData: registrations.length > 0,
            tone: registrations.some(g => g.expired) ? 'declined' : null },
        { id: 'connected', title: 'Connected businesses and associates', linkTitle: 'Connected', main: true, count: connected.length, hasData: connected.length > 0 },
        { id: 'payments', title: 'Payment history', linkTitle: 'Payments', main: true, count: payments?.trades || 0, hasData: Boolean(payments),
            tone: latePayer ? 'declined' : payments?.tone === 'watch' ? 'watch' : null },
        { id: 'records', title: 'Derogatory records', linkTitle: 'Records', main: true, count: derogatoryCount, hasData: derogatoryCount + experianDerogatory.length > 0,
            tone: openLiens || bankruptcies || experianOpenRecords ? 'declined' : null },
        { id: 'size', title: 'Company profile', linkTitle: 'Profile', count: estimates.length, hasData: estimates.length + changeAlerts.length + profileDetails.length > 0,
            tone: recentOwnerChange ? 'watch' : null, main: recentOwnerChange },
        { id: 'locations', title: 'Locations', count: locations.length, hasData: locations.length > 0 },
        { id: 'assets', title: 'Assets', count: assets.length, hasData: assets.length + assetGroups.length > 0,
            tone: propertyDistress ? 'declined' : null, main: propertyDistress > 0 },
        { id: 'professionalLicenses', title: 'Professional licenses', count: licenses.length, hasData: licenses.length > 0,
            tone: licenses.some(l => l.expired) ? 'declined' : licenses.some(l => l.expiresSoon) ? 'watch' : null, main: licenses.some(l => l.expired) },
        { id: 'pension', title: 'Pension plan filings', count: pensionPlans.length, hasData: pensionPlans.length > 0,
            tone: pensionPlans.some(p => p.einDiffers) ? 'watch' : null },
        { id: 'asLender', title: 'UCC filings as lender', count: asLender.length, hasData: asLender.length + asLenderEnded.length > 0 },
    ]

    const none = limitedHistory ? 'None yet' : 'None'
    const pieces = (...parts) => parts.filter(Boolean).join(' · ')
    const experianActive = experianFilings.filter(f => !f.ended)
    const experianActiveUccs = experianActive.length
    const uccClaims = activeUccs.length ? { receivables: receivablesUccs, recent: recentUccs }
        : { receivables: experianActive.filter(f => f.receivables).length, recent: experianActive.filter(f => f.recent).length }
    const connectedWithRecords = connected.filter(c => c.flagged || c.hasRecords).length
    const expiredRegistrations = registrations.filter(g => g.expired).length
    const expiredLicenses = licenses.filter(l => l.expired).length
    const standingWarning = Boolean(summary.notActive || badFilings.length || filingsWithOpenWarnings || expiredRegistrations || expiredLicenses)
    const openRecords = openLiens + experianOpenRecords
    const otherBadFilings = badFilings.filter(f => f !== homeFiling).length
    const tiles = [
        {
            section: 'ucc', label: 'Lender claims',
            value: activeUccs.length || experianActiveUccs || none, unit: activeUccs.length || experianActiveUccs ? 'active' : '',
            sub: pieces(uccClaims.receivables && `${uccClaims.receivables} on receivables${activeUccs.length ? ' or all assets' : ''}`,
                uccClaims.recent && `${uccClaims.recent} recent`, !activeUccs.length && experianActiveUccs && 'from Experian')
                || (activeUccs.length ? `${securedParties.size} ${securedParties.size === 1 ? 'lender' : 'lenders'}` : 'No active UCC filings'),
            flag: uccClaims.receivables > 0 || uccClaims.recent > 0,
        },
        {
            section: 'people', label: 'People with records',
            value: people.length ? flaggedPeople : none, unit: people.length ? `of ${people.length}` : '',
            sub: pieces(deceasedCurrent && `${deceasedCurrent} listed as deceased`,
                connectedWithRecords && `${connectedWithRecords} connected ${connectedWithRecords === 1 ? 'business' : 'businesses'} with records`)
                || (people.length ? 'No derogatory records' : 'No people on file'),
            flag: flaggedPeople > 0 || deceasedCurrent > 0,
        },
        {
            section: 'filings', label: 'State standing',
            target: summary.notActive || badFilings.length || filingsWithOpenWarnings ? 'filings'
                : expiredRegistrations ? 'licenses' : expiredLicenses ? 'professionalLicenses' : 'filings',
            value: summary.notActive ? 'Not active'
                : incorporation ? (incorporation.tone === 'neutral' ? 'Not reported' : incorporation.status) : 'No filing',
            sub: pieces(otherBadFilings && `${otherBadFilings} other ${otherBadFilings === 1 ? 'filing' : 'filings'} not active`,
                filingsWithOpenWarnings && `${filingsWithOpenWarnings} open ${filingsWithOpenWarnings === 1 ? 'warning' : 'warnings'}`,
                expiredRegistrations && `${expiredRegistrations} ${expiredRegistrations === 1 ? 'registration' : 'registrations'} expired`,
                expiredLicenses && `${expiredLicenses} ${expiredLicenses === 1 ? 'license' : 'licenses'} expired`)
                || (incorporation ? STATES[homeFiling.Origin] || homeFiling.Origin || '' : 'No state filing found'),
            flag: standingWarning,
        },
        {
            section: 'records', label: 'Liens and bankruptcies',
            value: openRecords || bankruptcies || none,
            unit: openRecords ? 'open' : bankruptcies ? (bankruptcies === 1 ? 'bankruptcy' : 'bankruptcies') : '',
            sub: pieces(openRecords && bankruptcies && `${bankruptcies} ${bankruptcies === 1 ? 'bankruptcy' : 'bankruptcies'}`,
                (liens.length + experianExtra.length) && `${liens.length + experianExtra.length} in total`) || 'No records found',
            flag: openRecords > 0 || bankruptcies > 0,
        },
        {
            section: 'payments', label: 'Payments',
            value: payments?.days == null ? 'No data' : payments.days === 0 ? 'On time' : payments.days,
            unit: payments?.days ? `${payments.days === 1 ? 'day' : 'days'} late` : '',
            sub: payments?.days == null ? 'No supplier payments reported'
                : pieces(payments.industry != null && `industry average ${payments.industry}`, payments.worsening && 'getting later'),
            flag: latePayer,
        },
    ].filter(t => t.section !== 'payments' || payments?.days != null)

    const hasData = Boolean(best.CompanyName || best.BusinessIds)
    const allClear = hasData && !activeUccs.length && !experianActiveUccs && !flaggedPeople && !deceasedCurrent && !openLiens && !bankruptcies && !badFilings.length
        && !filingsWithOpenWarnings && !summary.notActive && !latePayer && !propertyDistress && !experianOpenRecords
        && !expiredRegistrations && !expiredLicenses && !sections.some(s => s.tone)

    return {
        hasData,
        summary,
        tiles,
        sections,
        allClear,
        limitedHistory: hasData && limitedHistory ? {
            age: historyAge,
            people: people.length,
            flaggedPeople,
            executivesElsewhere: people.filter(p => p.elsewhere).length,
            linkedPeople: linkedPeople.length,
            connected: connected.length,
        } : null,
        ucc: {
            experian: { active: experianFilings.filter(f => !f.ended), ended: experianFilings.filter(f => f.ended), collateral: experianCollateral },
            active: activeUccs, ended: endedUccs, securedParties: securedParties.size, recent: recentUccs, receivables: receivablesUccs,
        },
        people: { rows: people, flagged: flaggedPeople, deceasedCurrent, linked: linkedPeople, experian: officers },
        filings: { rows: filings, showNames: showFilingNames, notActive: badFilings.length, openWarnings: filingsWithOpenWarnings, agents },
        registrations,
        connected,
        estimates,
        estimateSources,
        changeAlerts,
        growth,
        profileDetails,
        locations: { rows: locations, states: locationStates, disconnected: locations.filter(l => l.chips.some(c => /disconnected/.test(c.label))).length },
        assets,
        derogatory: { bankruptcies, bankruptcyRows, liens, open: openLiens, experian: experianDerogatory, experianOpen: experianOpenRecords, experianExtra: experianExtra.length },
        payments,
        assetGroups,
        propertyDistress,
        licenses,
        pensionPlans,
        asLender,
        asLenderEnded,
    }
}
